"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useCheckout } from '@/context/CheckoutContext';
import { useUserMenu } from '@/context/UserMenuContext';
import { useOrderSidebar } from '@/context/OrderSidebarContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { X, ShoppingCart, RotateCcw, Loader2, CreditCard, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Heart, QrCode, CalendarDays, UserPlus, Check, Search, Users, PlusCircle, AlertTriangle } from '@/components/icons';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { QRCodeCanvas } from 'qrcode.react';
import FlipCounter from '@/components/FlipCounter';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { useSession } from '@/context/SessionContext';
import { useSyncLocalCart } from '@/hooks/useSyncLocalCart';
import { usePlayBeep } from '@/hooks/usePlayBeep';
import { saveTempCart, getTempCartAsItems, removeFromTempCart, updateTempCartItemQuantity, clearTempCart } from '@/lib/tempCart';
import { useToast } from '@/components/ui/use-toast';
import { cn, formatCurrency } from '@/lib/utils';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isSameDay, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { usePullToClose } from '@/hooks/usePullToClose';
import { motion, AnimatePresence } from 'framer-motion';
import { getOpenCartsForUser, removeCartItem, updateCartItem, clearCart, updateCartNotes, createNewCartForStore, updateCartItemsDetailsForStore } from '@/lib/supabase/carts';
import { getOrdersByUserId } from '@/lib/supabase/orders';
import { getStoreById, getUserStores } from '@/lib/supabase/stores';
import { QuantitySelector } from '@/components/QuantitySelector';
import { Store as StoreIcon } from '@/components/icons';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useFavorites } from '@/hooks/useFavorites';
import { usePlayFavoriteSound } from '@/hooks/usePlayFavoriteSound';
import { useVibrate } from '@/hooks/useVibrate';
import { PassengerDetailsSidebar, type Passenger } from '@/components/tours/PassengerDetailsSidebar';
import { getProductById } from '@/lib/supabase/products';
import { useContentSidebar } from '@/context/ContentSidebarContext';
import { buildSharedCheckoutUrl, parseSharedCartFromQuery } from '@/lib/cartShare';
import {
  shareCartWithUsers,
  getCartsSharedWithMe,
  getCartAccessList,
  removeCartAccess,
} from '@/lib/supabase/sharedCarts';
import { useScrollLock } from '@/hooks/useScrollLock';
import { createNotification, getProfilesByIds, getUserSubscriptions, getProfileById } from '@/lib/supabase';
import { supabase } from '@/lib/supabase/client';
import * as ActiveCartPref from '@/lib/activeCartPreference';
import { updateOrderStatus } from '@/lib/supabase/orders';
import {
  getActiveCartPreference,
  getActiveCartSnapshot,
  getActiveSharedCartExplicitByStore,
  setActiveCartPreference,
  setActiveCartSnapshot,
  setActiveSharedCartExplicitByStore,
  setActiveSharedCartThemePriority,
  setActiveSharedCartToken,
  setActiveSharedCartRole,
  setActiveSharedCartGuestOwnerColor,
} from '@/lib/activeCartPreference';
import {
  getDispatchStatusLabel,
  getDispatchStatusProgress,
  getStoreStatusLabel,
  getStoreStatusProgress,
} from '@/lib/orderStatusLabels';

const getOrderStatusTone = (status: string | null | undefined) => {
  if (status === 'fulfilled') {
    return {
      badge: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/25',
      dot: 'bg-emerald-500',
      card: 'border-emerald-500/20',
      ring: 'group-hover:shadow-[0_0_0_1px_rgba(16,185,129,0.18),0_18px_36px_rgba(16,185,129,0.12)]',
    };
  }

  if (status === 'cancelled') {
    return {
      badge: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/25',
      dot: 'bg-rose-500',
      card: 'border-rose-500/20',
      ring: 'group-hover:shadow-[0_0_0_1px_rgba(244,63,94,0.18),0_18px_36px_rgba(244,63,94,0.12)]',
    };
  }

  return {
    badge: 'bg-primary/15 text-primary border-primary/25',
    dot: 'bg-primary animate-pulse',
    card: 'border-primary/25',
    ring: 'group-hover:shadow-[0_0_0_1px_rgba(99,102,241,0.2),0_22px_44px_rgba(99,102,241,0.14)]',
  };
};

const CHECKOUT_ORDERS_DEBUG = false;
const ORDERS_PAGE_SIZE = 10;

const debugCheckoutOrders = (...args: any[]) => {
  if (!CHECKOUT_ORDERS_DEBUG) return;
};

const SHARED_CART_SHARE_DEBUG = false;
const ACTIVE_CART_UI_DEBUG = false;

const debugSharedCartShare = (...args: any[]) => {
  if (!SHARED_CART_SHARE_DEBUG) return;
};

const debugActiveCartUi = (...args: any[]) => {
  if (!ACTIVE_CART_UI_DEBUG) return;
};

const EXCLUDED_STORE_ROOTS = new Set([
  'inicio',
  'auth',
  'login',
  'signup',
  'chats',
  'chat',
  'tour',
  'map',
  'mapa',
  'checkout',
  'collections',
  'notificaciones',
  'configuracion',
  'transporte',
  'search-results',
  'print-room',
  'activity-log',
  'cargafacil',
  'notas',
  'orden',
  'products',
  'edit-product',
  'tiendas',
  'mercadopago',
  '24hs',
]);

const extractStoreSlugFromPathname = (pathname: string): string | null => {
  const segment = (pathname || '').split('/').filter(Boolean)[0] || '';
  if (!segment) return null;
  if (EXCLUDED_STORE_ROOTS.has(segment)) return null;
  return segment;
};

const toErrorMessage = (error: unknown): string => {
  if (!error) return 'Error desconocido';
  if (typeof error === 'string') return error;
  const maybeObj = error as any;
  return maybeObj?.message || maybeObj?.error_description || maybeObj?.details || JSON.stringify(maybeObj);
};

const isMissingRelationError = (error: unknown, relationName: string): boolean => {
  const message = toErrorMessage(error).toLowerCase();
  const relation = relationName.toLowerCase();
  return message.includes(`relation \"public.${relation}\" does not exist`)
    || message.includes(`relation \"${relation}\" does not exist`)
    || message.includes(`could not find the table 'public.${relation}'`)
    || message.includes(`could not find the table '${relation}'`);
};

const sanitizeForPostgrestOrIlike = (input: string): string => {
  return input
    .replace(/[(),]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

// ─── Rental helpers ───────────────────────────────────────────────────────────

const toLocalDateInputSidebar = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const shiftLocalDateInputSidebar = (dateInput: string, dayOffset: number): string => {
  const base = dateInput ? new Date(`${dateInput}T00:00:00`) : new Date();
  if (Number.isNaN(base.getTime())) return toLocalDateInputSidebar(new Date());
  base.setDate(base.getDate() + dayOffset);
  return toLocalDateInputSidebar(base);
};

const calcRentalDays = (startDate: string, endDate: string): number => {
  if (!startDate || !endDate) return 1;
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;
  const diff = end.getTime() - start.getTime();
  if (diff < 0) return 0;
  return Math.floor(diff / 86400000) + 1;
};

const parseDetailsSidebar = (raw: any): Record<string, any> => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch { return {}; }
  }
  return {};
};

const isStoreRentalItem = (item: any): boolean => {
  if (item?.products?.is_store_rental === true) return true;
  const details = parseDetailsSidebar(item?.products?.details);
  if (details?.is_store_rental === true) return true;
  const title = String(item?.products?.title || item?.title || '').toLowerCase();
  const desc = String(item?.products?.description || item?.description || '').toLowerCase();
  return (
    title.includes('alquiler de tienda') ||
    desc.includes('alquiler de tienda') ||
    String(details?.rental_target || '').toLowerCase().includes('store') ||
    String(details?.rental_target || '').toLowerCase().includes('tienda')
  );
};

const getDailyPrice = (item: any): number | null => {
  const details = parseDetailsSidebar(item?.products?.details);
  const billingMode = String(details?.billing_mode || '').toLowerCase();
  const isStorRental = details?.is_store_rental === true || item?.products?.is_store_rental === true;
  const rentalTarget = String(details?.rental_target || '').toLowerCase();
  const title = String(item?.products?.title || item?.title || '').toLowerCase();
  const desc = String(item?.products?.description || item?.description || '').toLowerCase();
  const treatAsPerDay =
    billingMode === 'per_day' ||
    isStorRental ||
    rentalTarget.includes('store') || rentalTarget.includes('tienda') ||
    title.includes('alquiler') || title.includes('rental') ||
    desc.includes('alquiler de tienda') || desc.includes('por d\u00eda') || desc.includes('por dia');
  if (!treatAsPerDay) return null;
  let daily = Number(details?.daily_price);
  if (!Number.isFinite(daily) || daily <= 0) daily = Number(item?.unit_price);
  if (!Number.isFinite(daily) || daily <= 0) daily = parseFloat(String(item?.unit_price).replace(/[^0-9.-]/g, '')) || 0;
  return Number.isFinite(daily) && daily > 0 ? daily : null;
};

const buildCalendarDays = (startDate: string, totalDays: number) => {
  const safeTotal = Math.max(0, Math.min(totalDays, 35));
  if (!startDate || safeTotal === 0) return [] as Array<{ iso: string; label: string; weekday: string }>;
  return Array.from({ length: safeTotal }, (_, i) => {
    const iso = shiftLocalDateInputSidebar(startDate, i);
    const date = new Date(`${iso}T00:00:00`);
    return {
      iso,
      label: String(date.getDate()),
      weekday: date.toLocaleDateString('es', { weekday: 'short' }).slice(0, 2),
    };
  });
};

const cartItemSorter = new Intl.Collator('es', { sensitivity: 'base', numeric: true });

const sortCartItemsConsistently = (items: any[]): any[] => {
  if (!Array.isArray(items) || items.length <= 1) return items || [];

  return [...items].sort((a: any, b: any) => {
    const aTitle = String(a?.products?.title || a?.title || '').trim();
    const bTitle = String(b?.products?.title || b?.title || '').trim();
    const byTitle = cartItemSorter.compare(aTitle, bTitle);
    if (byTitle !== 0) return byTitle;

    const aProductId = String(a?.product_id || a?.products?.id || '').trim();
    const bProductId = String(b?.product_id || b?.products?.id || '').trim();
    const byProductId = cartItemSorter.compare(aProductId, bProductId);
    if (byProductId !== 0) return byProductId;

    return cartItemSorter.compare(String(a?.id || ''), String(b?.id || ''));
  });
};

type SharedCheckoutPayload = NonNullable<ReturnType<typeof parseSharedCartFromQuery>>;

const normalizeSharedPayloadCart = (payload: SharedCheckoutPayload) => ({
  id: 'shared-cart-payload',
  shared_cart_id: `payload:${payload.store.id}`,
  store_id: payload.store.id,
  user_id: 'shared',
  status: 'open',
  created_at: payload.created_at || new Date().toISOString(),
  stores: {
    id: payload.store.id,
    name: payload.store.name,
    slug: payload.store.slug,
  },
  cart_items: sortCartItemsConsistently((payload.items || []).map((item) => ({
    id: item.product_id,
    product_id: item.product_id,
    quantity: Number(item.quantity) || 1,
    unit_price: Number(item.unit_price) || 0,
    products: {
      id: item.product_id,
      title: item.title || 'Producto',
      description: '',
      main_image_url: item.main_image_url || null,
      cover_url: null,
      image_urls: [],
      stock: item.stock ?? null,
      is_digital: item.is_digital ?? null,
      product_type: item.product_type ?? null,
      fixed_sale_price: Number(item.unit_price) || 0,
      store_id: payload.store.id,
    },
  }))),
  notes: null,
});

type ShareRecipient = {
  id: string;
  username?: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
};

type SharedCartActivityLog = {
  id: string;
  shared_cart_id: string;
  actor_user_id: string;
  action: 'added' | 'updated' | 'removed' | 'cleared';
  product_id?: string | null;
  product_title?: string | null;
  quantity_before?: number | null;
  quantity_after?: number | null;
  quantity_delta?: number | null;
  created_at: string;
};

export const CheckoutSidebar: React.FC = () => {
  const { sidebarOpen, closeSidebar, modalOpen, cancelCloseDelay, closeWithDelay, openPaymentSidebar, openSidebar, preferredStoreId } = useCheckout();
  const { openSidebar: openUserMenu } = useUserMenu();
  const { openSidebarFromCheckout } = useOrderSidebar();
  const location = useLocation();
  const [isClosing, setIsClosing] = useState(false);
  const [hasLocalCart, setHasLocalCart] = useState(false);
  const [isSyncingCart, setIsSyncingCart] = useState(false);
  const [syncCardDismissed, setSyncCardDismissed] = useState(false);
  const [syncMetadata, setSyncMetadata] = useState<{
    syncedRecords: { id: string }[];
    backupItems: any[];
    storeId: string;
    isUndoing: boolean;
  } | null>(null);
  const [sharedCartPayload, setSharedCartPayload] = useState<SharedCheckoutPayload | null>(null);
  
  // New state for cart data (similar to FavoritesSidebar)
  const [carts, setCarts] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [ordersViewMode, setOrdersViewMode] = useState<'date' | 'store'>('date');
  const [ordersDateFilter, setOrdersDateFilter] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [visibleOrdersCount, setVisibleOrdersCount] = useState(ORDERS_PAGE_SIZE);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [showOrdersHistory, setShowOrdersHistory] = useState(false);
  const [ordersHistoryLoaded, setOrdersHistoryLoaded] = useState(false);
  const [storeInfoMap, setStoreInfoMap] = useState<Record<string, { id: string; name: string; slug?: string }>>({});
  const [updatingItemIds, setUpdatingItemIds] = useState<Set<string>>(new Set());
  const [confirmClearCartState, setConfirmClearCartState] = useState<{ cartId: string; step: 1 | 2 } | null>(null);
  const [clearCountdown, setClearCountdown] = useState<{ cartId: string; storeId: string; count: number } | null>(null);
  const [expandedStoreKeys, setExpandedStoreKeys] = useState<Set<string>>(new Set());
  const [showPassengerWall, setShowPassengerWall] = useState(false);
  const [passengerWallLoading, setPassengerWallLoading] = useState(false);
  const [pendingPaymentCart, setPendingPaymentCart] = useState<any | null>(null);
  const [wallSelectedTours, setWallSelectedTours] = useState<any[]>([]);
  const [wallInitialPassengerCount, setWallInitialPassengerCount] = useState(1);
  const [wallInitialPassengers, setWallInitialPassengers] = useState<Passenger[]>([]);
  const [wallInitialTourDays, setWallInitialTourDays] = useState<any[]>([]);
  const [expandedQRCartId, setExpandedQRCartId] = useState<string | null>(null);
  const [expandedSharePeopleCartId, setExpandedSharePeopleCartId] = useState<string | null>(null);
  const [shareRecipients, setShareRecipients] = useState<ShareRecipient[]>([]);
  const [platformSearchRecipients, setPlatformSearchRecipients] = useState<ShareRecipient[]>([]);
  const [shareUserSearchTerm, setShareUserSearchTerm] = useState('');
  const [selectedShareRecipientIdsByCartId, setSelectedShareRecipientIdsByCartId] = useState<Record<string, string[]>>({});
  const [sharedMembersByCartId, setSharedMembersByCartId] = useState<Record<string, ShareRecipient[]>>({});
  const [loadingSharedMembersByCartId, setLoadingSharedMembersByCartId] = useState<Record<string, boolean>>({});
  const [pendingSharedMemberRemoval, setPendingSharedMemberRemoval] = useState<{
    cartId: string;
    userId: string;
    displayName: string;
    remaining: number;
  } | null>(null);
  const [loadingShareRecipients, setLoadingShareRecipients] = useState(false);
  const [loadingPlatformSearchRecipients, setLoadingPlatformSearchRecipients] = useState(false);
  const [sharingByCartId, setSharingByCartId] = useState<Record<string, boolean>>({});
  const [pendingOrderCancel, setPendingOrderCancel] = useState<any | null>(null);
  const [sharedCartOwnerNameByCartId, setSharedCartOwnerNameByCartId] = useState<Record<string, string>>({});
  const [sharedCartOwnerColorByCartId, setSharedCartOwnerColorByCartId] = useState<Record<string, string | null>>({});
  const [sharedCartStoreSlugByCartId, setSharedCartStoreSlugByCartId] = useState<Record<string, string>>({});
  const [sharedCartIdByCartId, setSharedCartIdByCartId] = useState<Record<string, string>>({});
  const [sharedActivityByCartId, setSharedActivityByCartId] = useState<Record<string, SharedCartActivityLog[]>>({});
  const [sharedActivityActorNameByUserId, setSharedActivityActorNameByUserId] = useState<Record<string, string>>({});
  const [sharedActivityActorColorByUserId, setSharedActivityActorColorByUserId] = useState<Record<string, string | null>>({});
  const [activeCartIdByStore, setActiveCartIdByStore] = useState<Record<string, string>>({});
  const [creatingNewCartStoreId, setCreatingNewCartStoreId] = useState<string | null>(null);
  const loadCartsRunRef = useRef(0);
  const loadOrdersRunRef = useRef(0);
  const loadedForCurrentOpenRef = useRef(false);
  const lastLoadedSessionIdRef = useRef<string | null>(null);
  const sharedCartRealtimeReloadRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const parseTourPassengerMeta = useCallback((notes: string | null | undefined) => {
    if (!notes) return null;
    try {
      const parsed = JSON.parse(notes);
      if (parsed?.type !== 'tour_cart_passenger_details') return null;
      
      // Validate itinerary structure
      if (Array.isArray(parsed?.itinerary)) {
        const validItinerary = parsed.itinerary.filter((it: any) => {
          if (!it.date) {
            console.warn('⚠️ [CheckoutSidebar] Itinerary entry missing date:', it);
            return false;
          }
          return true;
        });
        if (validItinerary.length !== parsed.itinerary.length) {
          console.warn('⚠️ [CheckoutSidebar] Filtered invalid itinerary entries', { before: parsed.itinerary.length, after: validItinerary.length });
          return { ...parsed, itinerary: validItinerary };
        }
      }
      
      console.log('✅ [CheckoutSidebar] Parsed tour meta successfully:', { passengers: parsed?.passengers?.length, itinerary: parsed?.itinerary?.length });
      return parsed;
    } catch (e) {
      console.error('❌ [CheckoutSidebar] Error parsing tour passenger meta:', e, notes);
      return null;
    }
  }, []);

  const getPassengerMetaFromCartItems = useCallback((cartItems: any[]) => {
    if (!Array.isArray(cartItems) || cartItems.length === 0) {
      console.log('ℹ️ [CheckoutSidebar] No cart items to check for details');
      return null;
    }
    
    console.log('🔍 [CheckoutSidebar] Checking cart items for passenger details:', {
      itemCount: cartItems.length,
      items: cartItems.map(item => ({
        id: item?.id,
        hasDetails: !!item?.details,
        detailsKeys: item?.details ? Object.keys(item.details) : []
      }))
    });
    
    // Try to get details from the first cart item that has them
    for (const item of cartItems) {
      if (item?.details && typeof item.details === 'object') {
        const details = item.details;
        if (details.passengers || details.tourDays) {
          console.log('✅ [CheckoutSidebar] Found passenger details in cart_items.details:', { 
            passengers: details.passengers?.length, 
            tourDays: details.tourDays?.length 
          });
          return {
            type: 'tour_cart_passenger_details',
            passengers: details.passengers || [],
            itinerary: (details.tourDays || []).map((td: any) => ({
              date: td.date,
              tourId: td.tourId,
              tourTitle: td.tourTitle,
              destination: td.destination,
            })),
            pickup: details.pickupLocation,
            totalPrice: details.totalPrice,
            passengerCount: details.passengerCount,
            updatedAt: details.updatedAt,
          };
        }
      }
    }
    console.log('⚠️ [CheckoutSidebar] No passenger details found in any cart_items');
    return null;
  }, []);
  
  const sidebarRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { headerHeight, isDesktop, recalculate } = useHeaderOffset();
  const { session, profile } = useSession();
  const { syncLocalCartToDB, undoSyncCart } = useSyncLocalCart();
  const { toast } = useToast();
  const { isFavorited, toggleFavorite } = useFavorites();
  const playFavoriteSound = usePlayFavoriteSound();
  const playBeep = usePlayBeep();
  const vibrate = useVibrate();
  const { setSelectedProduct, setProductSidebarOpen } = useContentSidebar();
  const [loadingProductId, setLoadingProductId] = useState<string | null>(null);
  const [rentalDatesByCartId, setRentalDatesByCartId] = useState<Record<string, { startDate: string; endDate: string }>>({});
  const [rentalModeByCartId, setRentalModeByCartId] = useState<Record<string, 'nueva' | 'renovar'>>({});
  const [rentalStoreByCartId, setRentalStoreByCartId] = useState<Record<string, string | null>>({});
  const [rentalModeValidationByCartId, setRentalModeValidationByCartId] = useState<Record<string, boolean>>({});
  const [userStores, setUserStores] = useState<any[]>([]);
  const [loadingUserStores, setLoadingUserStores] = useState(false);
  const userStoresLoadedRef = React.useRef(false);

  useEffect(() => {
    const payload = parseSharedCartFromQuery(location.search);

    setSharedCartPayload(payload);
  }, [location.search]);

  // Initialize rental dates when carts load
  useEffect(() => {
    if (!Array.isArray(carts) || carts.length === 0) return;
    const today = toLocalDateInputSidebar(new Date());
    setRentalDatesByCartId((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const cart of carts) {
        if (next[cart.id]) continue;
        const items = cart?.cart_items || [];
        const hasRental = items.some((item: any) => getDailyPrice(item) !== null);
        if (!hasRental) continue;
        const qty = Math.max(items.reduce((s: number, it: any) => s + (getDailyPrice(it) !== null ? Number(it.quantity || 1) : 0), 0), 1);
        next[cart.id] = { startDate: today, endDate: shiftLocalDateInputSidebar(today, qty - 1) };
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [carts]);

  // Load user stores once when a store-rental cart is detected
  const hasStoreRentalCart = React.useMemo(
    () => carts.some((cart) => (cart?.cart_items || []).some((item: any) => isStoreRentalItem(item))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carts.length, carts.map((c) => c.id).join(',')]
  );
  useEffect(() => {
    if (!hasStoreRentalCart || !session?.user?.id || userStoresLoadedRef.current) return;
    userStoresLoadedRef.current = true;
    setLoadingUserStores(true);
    getUserStores()
      .then((stores) => setUserStores(stores))
      .catch((err) => { console.error(err); userStoresLoadedRef.current = false; })
      .finally(() => setLoadingUserStores(false));
  }, [hasStoreRentalCart, session?.user?.id]);

  const ordersForCurrentView = React.useMemo(() => {
    if (ordersViewMode !== 'date' || !ordersDateFilter) return orders;

    return orders.filter((order: any) => {
      if (!order?.created_at) return false;
      const createdAt = new Date(order.created_at);
      if (Number.isNaN(createdAt.getTime())) return false;
      const year = createdAt.getFullYear();
      const month = String(createdAt.getMonth() + 1).padStart(2, '0');
      const day = String(createdAt.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}` === ordersDateFilter;
    });
  }, [orders, ordersDateFilter, ordersViewMode]);
  // Separar pedidos en proceso y completados
  const allInProgressOrders = orders.filter((order: any) => order.status !== 'fulfilled' && order.status !== 'cancelled');
  const completedOrders = ordersForCurrentView.filter((order: any) => order.status === 'fulfilled' || order.status === 'cancelled');
  const visibleOrders = completedOrders.slice(0, visibleOrdersCount);
  const hasMoreOrders = completedOrders.length > visibleOrdersCount;
  const visibleOrdersByStore = (() => {
    const groups = new Map<string, { storeKey: string; storeName: string; orders: any[] }>();
    completedOrders.forEach((order: any) => {
      const storeKey = order.store_id || 'unknown';
      const storeName = order.stores?.name || 'Tienda';
      if (!groups.has(storeKey)) groups.set(storeKey, { storeKey, storeName, orders: [] });
      groups.get(storeKey)!.orders.push(order);
    });
    return Array.from(groups.values());
  })();

  const dedupeCarts = useCallback((inputCarts: any[]) => {
    if (!Array.isArray(inputCarts) || inputCarts.length <= 1) return inputCarts;

    const seen = new Set<string>();
    return inputCarts.filter((cart: any) => {
      const key = cart?.shared_cart_id
        ? `shared:${String(cart.shared_cart_id)}`
        : (cart?.id ? `cart:${String(cart.id)}` : null);

      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, []);

  const orderedCartsForDisplay = React.useMemo(() => {
    const uniqueCarts = dedupeCarts(carts);
    if (!Array.isArray(uniqueCarts) || uniqueCarts.length <= 1) return uniqueCarts;

    const groupedByStore = new Map<string, any[]>();
    const storeOrder: string[] = [];

    uniqueCarts.forEach((cart: any) => {
      const storeId = cart?.store_id || '__unknown__';
      if (!groupedByStore.has(storeId)) {
        groupedByStore.set(storeId, []);
        storeOrder.push(storeId);
      }
      groupedByStore.get(storeId)!.push(cart);
    });

    // If checkout was opened from a store (floating button), force that store first.
    if (preferredStoreId) {
      const preferredIdx = storeOrder.indexOf(preferredStoreId);
      if (preferredIdx > 0) {
        storeOrder.splice(preferredIdx, 1);
        storeOrder.unshift(preferredStoreId);
      }
    }

    const ordered: any[] = [];
    storeOrder.forEach((storeId) => {
      const cartsForStore = groupedByStore.get(storeId) || [];
      if (cartsForStore.length <= 1) {
        ordered.push(...cartsForStore);
        return;
      }

      const activeIdForStore = activeCartIdByStore[storeId];
      if (!activeIdForStore) {
        ordered.push(...cartsForStore);
        return;
      }

      const activeIdx = cartsForStore.findIndex((cart: any) => cart?.id === activeIdForStore);
      if (activeIdx <= 0) {
        ordered.push(...cartsForStore);
        return;
      }

      const next = [...cartsForStore];
      const [activeCart] = next.splice(activeIdx, 1);
      next.unshift(activeCart);
      ordered.push(...next);
    });

    return ordered;
  }, [carts, preferredStoreId, activeCartIdByStore, dedupeCarts]);

  const loadShareRecipients = useCallback(async () => {
    setLoadingShareRecipients(true);
    try {
      const followedIds = await getUserSubscriptions(session.user.id);

      const followedProfiles = followedIds.length > 0
        ? await getProfilesByIds(followedIds)
        : [];

      const { data: platformUsers, error: platformUsersError } = await supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url')
        .neq('id', session.user.id)
        .limit(50);

      if (platformUsersError) {
        console.error('[CheckoutSidebar] Error loading platform users:', platformUsersError);
      }

      const followedMapped: ShareRecipient[] = (followedProfiles || []).map((p) => ({
        id: p.id,
        username: p.username,
        display_name: p.display_name,
        avatar_url: p.avatar_url,
      }));

      const platformMapped: ShareRecipient[] = (platformUsers || []).map((p: any) => ({
        id: p.id,
        username: p.username,
        display_name: p.display_name,
        avatar_url: p.avatar_url,
      }));

      const dedupedById = new Map<string, ShareRecipient>();
      [...followedMapped, ...platformMapped].forEach((recipient) => {
        if (!dedupedById.has(recipient.id)) {
          dedupedById.set(recipient.id, recipient);
        }
      });

      setShareRecipients(Array.from(dedupedById.values()));
    } catch (e) {
      console.error('[CheckoutSidebar] Error loading users for sharing:', e);
      setShareRecipients([]);
    } finally {
      setLoadingShareRecipients(false);
    }
  }, [session?.user?.id]);

  const searchPlatformRecipients = useCallback(async (term: string) => {
    if (!session?.user?.id) {
      setPlatformSearchRecipients([]);
      return;
    }

    const normalizedTerm = term.trim();
    const safeTerm = sanitizeForPostgrestOrIlike(normalizedTerm);
    if (!safeTerm) {
      setPlatformSearchRecipients([]);
      return;
    }

    setLoadingPlatformSearchRecipients(true);
    try {
      const likeTerm = `%${safeTerm}%`;
      const byUsernameReq = supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url')
        .neq('id', session.user.id)
        .ilike('username', likeTerm)
        .limit(50);

      const byDisplayNameReq = supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url')
        .neq('id', session.user.id)
        .ilike('display_name', likeTerm)
        .limit(50);

      const [{ data: byUsername, error: byUsernameError }, { data: byDisplayName, error: byDisplayNameError }] = await Promise.all([
        byUsernameReq,
        byDisplayNameReq,
      ]);

      if (byUsernameError && byDisplayNameError) {
        console.error('[CheckoutSidebar] Error searching platform users:', byUsernameError);
        setPlatformSearchRecipients([]);
        return;
      }

      const mergedById = new Map<string, any>();
      [...(byUsername || []), ...(byDisplayName || [])].forEach((p: any) => {
        if (p?.id) mergedById.set(p.id, p);
      });

      setPlatformSearchRecipients(Array.from(mergedById.values()));
    } catch (e) {
      console.error('[CheckoutSidebar] Error searching platform users:', e);
      setPlatformSearchRecipients([]);
    } finally {
      setLoadingPlatformSearchRecipients(false);
    }
  }, [session?.user?.id]);

  useEffect(() => {
    if (!expandedSharePeopleCartId) return;

    const timer = setTimeout(() => {
      void searchPlatformRecipients(shareUserSearchTerm);
    }, 220);

    return () => clearTimeout(timer);
  }, [expandedSharePeopleCartId, shareUserSearchTerm, searchPlatformRecipients]);

  const handleToggleShareRecipient = useCallback((cartId: string, recipientId: string) => {
    setSelectedShareRecipientIdsByCartId((prev) => {
      const current = prev[cartId] || [];
      const exists = current.includes(recipientId);
      const next = exists ? current.filter((id) => id !== recipientId) : [...current, recipientId];
      return { ...prev, [cartId]: next };
    });
  }, []);

  const loadSharedMembersForCart = useCallback(async (cartId: string) => {
    if (!session?.user?.id || !cartId || cartId === 'temp-cart') {
      setSharedMembersByCartId((prev) => ({ ...prev, [cartId]: [] }));
      return;
    }

    setLoadingSharedMembersByCartId((prev) => ({ ...prev, [cartId]: true }));
    try {
      const access = await getCartAccessList(cartId);
      const normalizedMembers: ShareRecipient[] = (access || [])
        .map((item: any) => ({
          id: item.user_id,
          username: item.username,
          display_name: item.display_name,
          avatar_url: item.avatar_url,
        }))
        .filter((member: ShareRecipient) => !!member.id && member.id !== session.user.id);

      setSharedMembersByCartId((prev) => ({ ...prev, [cartId]: normalizedMembers }));
    } catch (error) {
      console.error('[CheckoutSidebar] Error loading shared members:', error);
      setSharedMembersByCartId((prev) => ({ ...prev, [cartId]: [] }));
    } finally {
      setLoadingSharedMembersByCartId((prev) => ({ ...prev, [cartId]: false }));
    }
  }, [session?.user?.id]);

  const scheduleSharedMemberRemoval = useCallback((cartId: string, userId: string, displayName: string) => {
    setPendingSharedMemberRemoval({
      cartId,
      userId,
      displayName,
      remaining: 4,
    });
  }, []);

  const cancelPendingSharedMemberRemoval = useCallback(() => {
    setPendingSharedMemberRemoval(null);
  }, []);

  useEffect(() => {
    if (!pendingSharedMemberRemoval) return;

    const intervalId = window.setInterval(() => {
      setPendingSharedMemberRemoval((prev) => {
        if (!prev) return null;
        if (prev.remaining <= 1) return null;
        return { ...prev, remaining: prev.remaining - 1 };
      });
    }, 1000);

    const timeoutId = window.setTimeout(async () => {
      const pending = pendingSharedMemberRemoval;
      setPendingSharedMemberRemoval(null);

      try {
        await removeCartAccess(pending.cartId, pending.userId);
        await loadSharedMembersForCart(pending.cartId);
        toast({
          title: 'Usuario removido',
          description: `${pending.displayName} ya no colabora en este canasto.`,
        });
      } catch (error) {
        console.error('[CheckoutSidebar] Error removing shared member:', error);
        toast({
          title: 'Error',
          description: `No pudimos remover al usuario. ${toErrorMessage(error)}`,
          variant: 'destructive',
        });
      }
    }, pendingSharedMemberRemoval.remaining * 1000);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [pendingSharedMemberRemoval, loadSharedMembersForCart, toast]);

  const handleShareCartWithSelectedUsers = useCallback(async (cart: any) => {
    if (!session?.user?.id) {
      openUserMenu();
      return;
    }

    const selectedIds = selectedShareRecipientIdsByCartId[cart.id] || [];
    if (!selectedIds.length) {
      toast({ title: 'Selecciona usuarios', description: 'Elige al menos un usuario para compartir el canasto.' });
      return;
    }

    setSharingByCartId((prev) => ({ ...prev, [cart.id]: true }));
    try {
      debugSharedCartShare('share:start', {
        cartId: cart?.id,
        storeId: cart?.store_id,
        selectedUserCount: selectedIds.length,
        selectedUserIds: selectedIds,
      });

      const shareCount = await shareCartWithUsers(cart.id, selectedIds);

      // shareCount can be 0 when all selected users already had access (e.g. store_member).
      // That is NOT an error — they can already access the cart.
      const alreadyHadAccess = shareCount === 0;

      const sharerName = profile?.username || 'Alguien';
      const storeInfo = storeInfoMap[cart.store_id];
      const storeSlug = storeInfo?.slug || cart?.stores?.slug;
      const link = storeSlug
        ? `/${storeSlug}?open_checkout=1`
        : `/checkout`;

      debugSharedCartShare('share:success', {
        cartId: cart?.id,
        sharedWithCount: shareCount,
        alreadyHadAccess,
      });

      // Only notify users that were newly added (shareCount > 0).
      if (!alreadyHadAccess) {
        await Promise.allSettled(
          selectedIds.map((userId) =>
            createNotification(
              userId,
              'order_update',
              `${sharerName} ha compartido un canasto contigo.`,
              link
            )
          )
        );
      }

      toast({
        title: 'Canasto compartido',
        description: alreadyHadAccess
          ? 'Los usuarios seleccionados ya tienen acceso a este canasto.'
          : `Se envió la invitación a ${shareCount} usuario${shareCount === 1 ? '' : 's'}.`,
      });

      await loadSharedMembersForCart(cart.id);
      setExpandedSharePeopleCartId(null);
      setSelectedShareRecipientIdsByCartId((prev) => ({ ...prev, [cart.id]: [] }));
    } catch (e) {
      console.error('[CheckoutSidebar] Error sharing cart with users:', e);
      debugSharedCartShare('share:error', {
        cartId: cart?.id,
        error: toErrorMessage(e),
        rawError: e,
      });
      toast({
        title: 'Error',
        description: `No pudimos compartir el canasto. ${toErrorMessage(e)}`,
        variant: 'destructive',
      });
    } finally {
      debugSharedCartShare('share:end', { cartId: cart?.id });
      setSharingByCartId((prev) => ({ ...prev, [cart.id]: false }));
    }
  }, [session?.user?.id, selectedShareRecipientIdsByCartId, storeInfoMap, profile?.username, toast, openUserMenu, loadSharedMembersForCart]);

  const renderOrderCard = useCallback((order: any) => {
    const store = order.stores;
    const statusTone = getOrderStatusTone(order.status);
    const storeStatusText = getStoreStatusLabel(order.status);
    const dispatchStatusText = getDispatchStatusLabel(order.dispatch_status);
    const dispatchStatusKey = String(order.dispatch_status || '').trim().toLowerCase();
    const canCancelBeforeCourier = !['picked_up', 'delivered', 'completed', 'cancelled'].includes(dispatchStatusKey);
    const storeStatusProgress = Math.min(100, Math.max(0, getStoreStatusProgress(order.status)));
    const dispatchStatusProgress = Math.min(100, Math.max(0, getDispatchStatusProgress(order.dispatch_status)));
    const fetchedStoreCost = order.subtotal ?? order.products_subtotal ?? order.total ?? null;
    const fetchedDeliveryCost = order.delivery_fee ?? order.delivery_total ?? order.shipping_total ?? null;
    const normalizedStoreCost = fetchedStoreCost != null ? Number(fetchedStoreCost) : null;
    const normalizedDeliveryCost = fetchedDeliveryCost != null ? Number(fetchedDeliveryCost) : null;
    const hasValidStoreCost = normalizedStoreCost !== null && Number.isFinite(normalizedStoreCost);
    const hasValidDeliveryCost = normalizedDeliveryCost !== null && Number.isFinite(normalizedDeliveryCost);
    const fetchedOrderTotal = order.total != null && Number.isFinite(Number(order.total))
      ? Number(order.total)
      : null;
    const totalPlusDelivery = fetchedOrderTotal !== null
      ? Number((fetchedOrderTotal + (normalizedDeliveryCost ?? 0)).toFixed(2))
      : null;
    const totalFromParts = hasValidStoreCost && hasValidDeliveryCost
      ? Number((normalizedStoreCost + normalizedDeliveryCost).toFixed(2))
      : null;

    return (
      <div
        key={order.id}
        role="button"
        tabIndex={0}
        onClick={() => openSidebarFromCheckout(order.id)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          openSidebarFromCheckout(order.id);
        }}
        className={cn(
          'group relative w-full min-w-0 overflow-hidden rounded-2xl border bg-gradient-to-br from-card via-card to-primary/5 p-3.5 text-left transition-all duration-200 ease-out outline-none',
          'hover:-translate-y-0.5 hover:border-primary/35 focus-visible:-translate-y-0.5 focus-visible:border-primary/35',
          statusTone.card,
          statusTone.ring
        )}
      >
        <div className="pointer-events-none absolute -right-16 -top-16 h-28 w-28 rounded-full bg-primary/10 blur-2xl opacity-70" />
        <div className="relative z-10 flex items-start justify-between gap-3 min-w-0">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            {store && (
              <Avatar className="h-11 w-11 self-start rounded-xl shrink-0 border border-primary/20 ring-1 ring-primary/10">
                {store.avatar_url ? <AvatarImage src={store.avatar_url} className="rounded-xl" /> : <AvatarFallback className="rounded-xl bg-primary/15 text-primary font-semibold">{(store.name || 'T')[0]}</AvatarFallback>}
              </Avatar>
            )}
            <div className="min-w-0 max-w-full overflow-hidden">
              <div className="font-semibold text-sm truncate text-foreground">{store?.name || 'Tienda'}</div>
              <div className="mt-0.5 text-xs text-muted-foreground truncate" title={order.order_number || order.id || '—'}>
                #{order.order_number || order.id?.slice(0, 8) || '—'}
              </div>
              <span className="mt-1 inline-flex items-center text-[11px] text-muted-foreground whitespace-nowrap">
                {order.created_at ? format(new Date(order.created_at), 'dd/MM/yyyy HH:mm') : '—'}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0 min-w-0 max-w-[12rem]">
            <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground/80 truncate">Total</p>
            <p className="text-base font-extrabold text-primary leading-tight truncate">
              {totalPlusDelivery != null ? formatCurrency(totalPlusDelivery) : (totalFromParts != null ? formatCurrency(totalFromParts) : '—')}
            </p>
          </div>
        </div>

        <div className="relative z-10 mt-3 rounded-lg border border-border/60 bg-background/70 p-2">
          <div className="grid grid-cols-1 gap-2">
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-medium text-muted-foreground">Tienda ({storeStatusText})</span>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                  {normalizedStoreCost != null ? formatCurrency(normalizedStoreCost) : '—'} · {storeStatusProgress}%
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-300"
                  style={{ width: `${storeStatusProgress}%` }}
                />
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-medium text-muted-foreground">Envío ({dispatchStatusText})</span>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                  {normalizedDeliveryCost != null ? formatCurrency(normalizedDeliveryCost) : '—'} · {dispatchStatusProgress}%
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                  style={{ width: `${dispatchStatusProgress}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {dispatchStatusKey === 'delivered' ? null : (
          <div className="mt-2 space-y-2">
            {canCancelBeforeCourier ? (
              <AnimatePresence mode="wait" initial={false}>
                {pendingOrderCancel?.id === order.id ? (
                  <motion.div
                    key="confirm-cancel"
                    initial={{ opacity: 0, y: -4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.98 }}
                    transition={{ duration: 0.16, ease: 'easeOut' }}
                    className="space-y-2 rounded-xl border border-destructive/20 bg-destructive/5 p-3"
                  >
                    <p className="text-xs font-medium text-destructive leading-tight">
                      Seguro que quieres cancelar este pedido. La tienda gestionará tu devolución.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setPendingOrderCancel(null);
                        }}
                        className="flex-1 rounded-full border border-border bg-background px-3 py-2 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted"
                      >
                        No Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          void confirmPendingOrderCancel();
                        }}
                        className="flex-1 rounded-full border border-destructive/25 bg-destructive/10 px-3 py-2 text-xs font-semibold text-destructive transition-colors hover:bg-destructive/15"
                      >
                        Cancelar
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.button
                    key="cancel-button"
                    type="button"
                    initial={{ opacity: 0, y: 4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 4, scale: 0.98 }}
                    transition={{ duration: 0.16, ease: 'easeOut' }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setPendingOrderCancel(order);
                    }}
                    className="inline-flex w-fit max-w-full items-center justify-start gap-1.5 rounded-full px-0 py-0.5 text-[11px] font-normal text-muted-foreground/70 transition-colors hover:text-muted-foreground"
                  >
                    <X className="h-3 w-3" />
                    Cancelar pedido
                  </motion.button>
                )}
              </AnimatePresence>
            ) : (
              <div className="space-y-2">
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-border bg-muted/60 px-3 py-2 text-xs font-semibold text-muted-foreground/80 opacity-75 cursor-not-allowed"
                >
                  <X className="h-3.5 w-3.5" />
                  Cancelar pedido
                </button>
                <p className="text-[11px] leading-snug text-muted-foreground">
                  Tu repartidor ya está yendo a entregarte el pedido. Recíbelo y gestiona la devolución con la tienda. Debes pagar {normalizedDeliveryCost != null ? formatCurrency(normalizedDeliveryCost) : 'el costo del envío'} al repartidor por su servicio canceles o no.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }, [confirmPendingOrderCancel, openSidebarFromCheckout, pendingOrderCancel]);

  // Calculate total cart price
  const calculateTotal = useCallback(() => {
    return carts.reduce((total: number, cart: any) => {
      const cartTotal = cart.cart_items?.reduce((sum: number, item: any) => {
        const unitPrice = Number(item?.products?.fixed_sale_price ?? item?.unit_price ?? 0);
        return sum + (unitPrice * Number(item?.quantity || 1));
      }, 0) || 0;
      return total + cartTotal;
    }, 0);
  }, [carts]);

  // Sort carts so the preferred store appears first
  const sortByPreferred = useCallback((inputCarts: any[]) => {
    if (!Array.isArray(inputCarts) || inputCarts.length <= 1) return inputCarts;
    
    const result = [...inputCarts];
    
    // Find and move active cart to the top
    const activeCartId = Object.values(activeCartIdByStore)[0];
    if (activeCartId) {
      const activeIdx = result.findIndex((c) => c?.id === activeCartId);
      if (activeIdx > 0) {
        const [active] = result.splice(activeIdx, 1);
        result.unshift(active);
      }
    }
    
    // Then group by store and keep preferred store at top
    if (preferredStoreId) {
      const grouped: Record<string, any[]> = {};
      const order: string[] = [];

      result.forEach((cart) => {
        const storeId = cart?.store_id;
        if (!grouped[storeId]) {
          grouped[storeId] = [];
          order.push(storeId);
        }
        grouped[storeId].push(cart);
      });

      if (!result[0] || result[0].store_id !== preferredStoreId) {
        const prefIdx = order.indexOf(preferredStoreId);
        if (prefIdx > 0) {
          order.splice(prefIdx, 1);
          order.splice(1, 0, preferredStoreId);
        }
      }

      const reordered: any[] = [];
      order.forEach((storeId) => {
        reordered.push(...grouped[storeId]);
      });
      return reordered;
    }

    return result;
  }, [preferredStoreId, activeCartIdByStore]);

  // Load carts (similar logic to CheckoutPage)
  const loadCarts = useCallback(async () => {
    const runId = ++loadCartsRunRef.current;
    console.log('📦 [CheckoutSidebar] loadCarts started, runId:', runId);
    debugCheckoutOrders('loadCarts:start', { runId, userId: session?.user?.id || null, sidebarOpen });
    try {
      const sharedPayloadCart = sharedCartPayload ? normalizeSharedPayloadCart(sharedCartPayload) : null;

      if (session?.user?.id) {
        const openCarts = await getOpenCartsForUser(session.user.id);
        console.log('📦 [CheckoutSidebar] Loaded carts:', { runId, count: openCarts?.length, carts: openCarts?.map((c: any) => ({ id: c.id, storeId: c.store_id, notes: c.notes?.substring(0, 100) })) });
        debugCheckoutOrders('loadCarts:openCarts:resolved', { runId, count: openCarts?.length || 0 });

        const cleanedCarts = (openCarts || []).map((cart: any) => ({
          ...cart,
          cart_items: (cart.cart_items || []).filter((item: any) =>
            !item.products?.store_id || item.products.store_id === cart.store_id
          ),
        }));
        const nextCarts = [...cleanedCarts];

        const ids = (openCarts || []).map((c: any) => c.store_id).filter(Boolean);
        const uniqueIds = Array.from(new Set(ids));
        const map: Record<string, { id: string; name: string; slug?: string }> = {};

        await Promise.all(uniqueIds.map(async (id) => {
          const store = await getStoreById(id);
          if (store) map[id] = { id: store.id, name: store.name, slug: store.slug };
        }));

        try {
          const sharedWithMe = await getCartsSharedWithMe();
          const existingSharedIds = new Set(
            nextCarts
              .map((cart: any) => cart?.shared_cart_id)
              .filter(Boolean)
          );

          if (sharedPayloadCart && !existingSharedIds.has(sharedPayloadCart.shared_cart_id)) {
            nextCarts.unshift(sharedPayloadCart);
            map[sharedPayloadCart.store_id] = {
              id: sharedPayloadCart.store_id,
              name: sharedPayloadCart.stores.name,
              slug: sharedPayloadCart.stores.slug || undefined,
            };
            existingSharedIds.add(sharedPayloadCart.shared_cart_id);
          }

          const ownerIds = Array.from(new Set(
            (sharedWithMe || [])
              .map((shared: any) => shared?.user_id)
              .filter((ownerId: any) => !!ownerId)
          ));
          const ownerProfiles = ownerIds.length > 0
            ? await getProfilesByIds(ownerIds)
            : [];
          const ownerProfilesById = new Map((ownerProfiles || []).map((owner: any) => [owner.id, owner]));

          const nextOwnerNamesByCartId: Record<string, string> = {};
          const nextOwnerColorsByCartId: Record<string, string | null> = {};
          const nextStoreSlugByCartId: Record<string, string> = {};

          (sharedWithMe || []).forEach((shared: any) => {
            if (!shared?.id || existingSharedIds.has(shared.id)) return;

            const sharedStoreId = shared.store_id;
            const sharedStoreName = shared.store_name || 'Tienda';
            const sharedStoreSlug = shared.store_slug || null;

            const sharedCartItems = (shared.items || []).map((item: any) => ({
              id: item.id,
              product_id: item.product_id,
              quantity: Number(item.quantity) || 1,
              unit_price: Number(item.unit_price) || 0,
              products: {
                id: item.product_id,
                title: item.title || 'Producto',
                description: item.description || '',
                main_image_url: item.main_image_url,
                cover_url: item.cover_url,
                image_urls: item.image_urls,
                stock: item.stock,
                is_digital: item.is_digital,
                product_type: item.product_type,
                fixed_sale_price: Number(item.fixed_sale_price ?? item.unit_price) || 0,
                store_id: sharedStoreId,
              },
            }));

            const sharedCart = {
              id: `shared-cart-${shared.id}`,
              shared_cart_id: shared.id,
              store_id: sharedStoreId,
              user_id: 'shared',
              status: 'open',
              created_at: shared.updated_at || new Date().toISOString(),
              stores: {
                id: sharedStoreId,
                name: sharedStoreName,
                slug: sharedStoreSlug,
              },
              cart_items: sharedCartItems,
              notes: null,
              shared_by_user_id: shared.shared_by_user_id || null,
            };

            nextCarts.unshift(sharedCart);
            existingSharedIds.add(shared.id);

            map[sharedStoreId] = {
              id: sharedStoreId,
              name: sharedStoreName,
              slug: sharedStoreSlug || undefined,
            };

            if (sharedStoreSlug) {
              nextStoreSlugByCartId[sharedCart.id] = sharedStoreSlug;
            }

            const owner = ownerProfilesById.get(shared.user_id);
            if (owner) {
              nextOwnerNamesByCartId[sharedCart.id] = owner.display_name || owner.username || 'un usuario';
              nextOwnerColorsByCartId[sharedCart.id] = typeof owner?.appearance_settings?.customPrimary === 'string'
                ? owner.appearance_settings.customPrimary
                : null;
            } else {
              nextOwnerNamesByCartId[sharedCart.id] = 'un usuario';
              nextOwnerColorsByCartId[sharedCart.id] = null;
            }
          });

          if (Object.keys(nextOwnerNamesByCartId).length > 0) {
            setSharedCartOwnerNameByCartId((prev) => ({ ...prev, ...nextOwnerNamesByCartId }));
          }
          if (Object.keys(nextOwnerColorsByCartId).length > 0) {
            setSharedCartOwnerColorByCartId((prev) => ({ ...prev, ...nextOwnerColorsByCartId }));
          }
          if (Object.keys(nextStoreSlugByCartId).length > 0) {
            setSharedCartStoreSlugByCartId((prev) => ({ ...prev, ...nextStoreSlugByCartId }));
          }
        } catch (sharedWithMeError) {
          console.error('[CheckoutSidebar] Error loading shared carts with me:', sharedWithMeError);
        }

        setCarts(sortByPreferred(dedupeCarts(nextCarts)));
        setStoreInfoMap(map);
        debugCheckoutOrders('loadCarts:storeInfo:resolved', { runId, storeCount: Object.keys(map).length });
      } else {
        const tempItems = getTempCartAsItems();
        const nextCarts: any[] = [];
        const nextStoreInfoMap: Record<string, { id: string; name: string; slug?: string }> = {};

        if (sharedPayloadCart) {
          nextCarts.push(sharedPayloadCart);
          nextStoreInfoMap[sharedPayloadCart.store_id] = {
            id: sharedPayloadCart.store_id,
            name: sharedPayloadCart.stores.name,
            slug: sharedPayloadCart.stores.slug || undefined,
          };
        }

        debugCheckoutOrders('loadCarts:anonTempItems', { runId, count: tempItems.length });
        if (tempItems.length > 0) {
          const tempCart = {
            id: 'temp-cart',
            store_id: '30e79df7-81e8-4c5f-a825-602e5899d961',
            user_id: 'anonymous',
            cart_items: tempItems,
            status: 'open',
            created_at: new Date().toISOString(),
            stores: { name: 'Minibid 24hs' },
          };
          nextCarts.push(tempCart);
          nextStoreInfoMap['30e79df7-81e8-4c5f-a825-602e5899d961'] = { id: tempCart.store_id, name: 'Minibid 24hs' };
        }

        if (nextCarts.length > 0) {
          setCarts(sortByPreferred(dedupeCarts(nextCarts)));
          setStoreInfoMap(nextStoreInfoMap);
        } else {
          setCarts([]);
          setStoreInfoMap({});
        }
      }
    } catch (e) {
      debugCheckoutOrders('loadCarts:error', { runId, error: e });
    } finally {
      debugCheckoutOrders('loadCarts:end', { runId });
    }
  }, [session?.user?.id, sortByPreferred, sidebarOpen]);

  const queueLiveCartReload = useCallback(() => {
    if (sharedCartRealtimeReloadRef.current) return;
    sharedCartRealtimeReloadRef.current = setTimeout(() => {
      sharedCartRealtimeReloadRef.current = null;
      void loadCarts();
      window.dispatchEvent(new CustomEvent('cart-updated'));
    }, 120);
  }, [loadCarts]);

  const syncActiveCartSnapshotForCart = useCallback((targetCart: any) => {
    if (!targetCart?.id || !targetCart?.store_id) return;

    const items = targetCart.cart_items || [];
    const units = items.reduce((sum: number, cartItem: any) => sum + Number(cartItem?.quantity || 0), 0);
    const total = items.reduce((sum: number, cartItem: any) => {
      const unitPrice = Number(cartItem?.products?.fixed_sale_price ?? cartItem?.unit_price ?? 0);
      return sum + (unitPrice * Number(cartItem?.quantity || 0));
    }, 0);

    const previousSnapshot = getActiveCartSnapshot(targetCart.store_id);
    const isCollaborativeSelection = !!targetCart?.shared_cart_id;
    const shouldRefreshSnapshot = previousSnapshot?.cartId === targetCart.id || activeCartIdByStore[targetCart.store_id] === targetCart.id;

    if (!shouldRefreshSnapshot) return;

    setActiveCartSnapshot({
      storeId: targetCart.store_id,
      cartId: targetCart.id,
      isShared: isCollaborativeSelection,
      sharedToken: null,
      units,
      total,
      storeName: targetCart?.stores?.name || storeInfoMap[targetCart.store_id]?.name || 'Tienda',
      storeSlug: targetCart?.stores?.slug || storeInfoMap[targetCart.store_id]?.slug,
      updatedAt: new Date().toISOString(),
    });
  }, [activeCartIdByStore, storeInfoMap]);

  const loadSharedCartActivity = useCallback(async () => {
    if (!sidebarOpen || !session?.user?.id) return;

    const targetSharedCarts = (carts || []).filter((cart: any) => !!cart?.id && !!cart?.shared_cart_id);
    if (targetSharedCarts.length === 0) {
      setSharedActivityByCartId({});
      setSharedCartIdByCartId({});
      return;
    }

    const nextActivityByCartId: Record<string, SharedCartActivityLog[]> = {};
    const nextSharedCartIdByCartId: Record<string, string> = {};
    targetSharedCarts.forEach((cart: any) => {
      const sharedCartId = cart.shared_cart_id as string | undefined;
      if (!sharedCartId) return;

      nextSharedCartIdByCartId[cart.id] = sharedCartId;
      nextActivityByCartId[cart.id] = [];
    });

    const sharedCartIds = Object.values(nextSharedCartIdByCartId);
    if (sharedCartIds.length > 0) {
      const { data: logsData, error: logsError } = await supabase
        .from('shared_cart_activity_logs')
        .select('id, shared_cart_id, actor_user_id, action, product_id, product_title, quantity_before, quantity_after, quantity_delta, created_at')
        .in('shared_cart_id', sharedCartIds)
        .order('created_at', { ascending: false })
        .limit(150);

      if (logsError) {
        console.error('[CheckoutSidebar] Error loading shared cart activity logs:', logsError);
      } else {
        const logsBySharedCartId = new Map<string, SharedCartActivityLog[]>();
        (logsData || []).forEach((log: SharedCartActivityLog) => {
          const bucket = logsBySharedCartId.get(log.shared_cart_id) || [];
          bucket.push(log);
          logsBySharedCartId.set(log.shared_cart_id, bucket);
        });

        Object.entries(nextSharedCartIdByCartId).forEach(([cartId, sharedCartId]) => {
          nextActivityByCartId[cartId] = (logsBySharedCartId.get(sharedCartId) || []).slice(0, 20);
        });

        const actorIds = Array.from(new Set(
          (logsData || [])
            .map((log: any) => log?.actor_user_id)
            .filter((actorId: any) => !!actorId)
        )) as string[];

        const unresolvedActorIds = actorIds.filter((actorId) => !sharedActivityActorNameByUserId[actorId]);
        if (unresolvedActorIds.length > 0) {
          try {
            const actorProfiles = await getProfilesByIds(unresolvedActorIds);
            const nextActorNames: Record<string, string> = {};
            const nextActorColors: Record<string, string | null> = {};

            (actorProfiles || []).forEach((profile: any) => {
              if (!profile?.id) return;
              nextActorNames[profile.id] = profile.display_name || profile.username || 'Alguien';
              nextActorColors[profile.id] = typeof profile?.appearance_settings?.customPrimary === 'string'
                ? profile.appearance_settings.customPrimary
                : null;
            });

            if (Object.keys(nextActorNames).length > 0) {
              setSharedActivityActorNameByUserId((prev) => ({ ...prev, ...nextActorNames }));
            }
            if (Object.keys(nextActorColors).length > 0) {
              setSharedActivityActorColorByUserId((prev) => ({ ...prev, ...nextActorColors }));
            }
          } catch (actorError) {
            console.error('[CheckoutSidebar] Error loading shared activity actors:', actorError);
          }
        }
      }
    }

    setSharedActivityByCartId(nextActivityByCartId);
    setSharedCartIdByCartId(nextSharedCartIdByCartId);
  }, [sidebarOpen, session?.user?.id, carts, sharedActivityActorNameByUserId]);

  useEffect(() => {
    void loadSharedCartActivity();
  }, [loadSharedCartActivity]);

  // If the owner's active cart receives external collaborative activity,
  // emit a signal so the truth-manager promotes it to explicit collaborative mode.
  useEffect(() => {
    if (!session?.user?.id || !Array.isArray(carts) || carts.length === 0) return;

    Object.entries(activeCartIdByStore).forEach(([storeId, activeCartId]) => {
      const activeCart = carts.find((cart: any) => cart?.id === activeCartId && cart?.store_id === storeId);
      if (!activeCart || activeCart?.shared_cart_id) return;

      const logs = sharedActivityByCartId[activeCartId] || [];
      const hasExternalCollaboratorActivity = logs.some(
        (log) => !!log?.actor_user_id && log.actor_user_id !== session.user.id
      );
      if (!hasExternalCollaboratorActivity) return;

      const latestExternalActivity = logs.find(
        (log) => !!log?.actor_user_id && log.actor_user_id !== session.user.id
      );
      const collaboratorColor = latestExternalActivity?.actor_user_id
        ? (sharedActivityActorColorByUserId[latestExternalActivity.actor_user_id] ?? null)
        : null;

      const wasExplicit = getActiveSharedCartExplicitByStore(storeId);
      const previousSnapshot = getActiveCartSnapshot(storeId);
      const snapshotAlreadyCollaborative = !!previousSnapshot?.isShared && previousSnapshot?.cartId === activeCartId;

      if (wasExplicit && snapshotAlreadyCollaborative) {
        return;
      }

      const derivedItems = activeCart?.cart_items || [];
      const validDerivedItems = derivedItems.filter((item: any) => Number(item?.quantity || 0) > 0);
      const derivedUnits = validDerivedItems.reduce((sum: number, item: any) => sum + Number(item?.quantity || 0), 0);
      const derivedTotal = derivedItems.reduce((sum: number, item: any) => {
        const unitPrice = Number(item?.products?.fixed_sale_price ?? item?.unit_price ?? 0);
        return sum + (item.quantity && Number(item.quantity) > 0 ? unitPrice * Number(item.quantity) : 0);
      }, 0);

      ActiveCartPref.emitCollaborativeActivitySignal?.({
        storeId,
        cartId: activeCartId,
        units: derivedUnits,
        total: derivedTotal,
        storeName: activeCart?.stores?.name || storeInfoMap[storeId]?.name || 'Tienda',
        storeSlug: activeCart?.stores?.slug || storeInfoMap[storeId]?.slug,
        collaboratorColor,
      });
    });
  }, [
    session?.user?.id,
    carts,
    activeCartIdByStore,
    sharedActivityByCartId,
    sharedActivityActorColorByUserId,
    storeInfoMap,
  ]);

  useEffect(() => {
    if (!Array.isArray(carts) || carts.length === 0) {
      setActiveCartIdByStore({});
      return;
    }

    const groupedByStore = new Map<string, any[]>();
    carts.forEach((cart) => {
      if (!cart?.store_id) return;
      const list = groupedByStore.get(cart.store_id) || [];
      list.push(cart);
      groupedByStore.set(cart.store_id, list);
    });

    const nextActiveByStore: Record<string, string> = {};
    groupedByStore.forEach((storeCarts, storeId) => {
      const storedCartId = getActiveCartPreference(storeId);
      const sharedExplicit = getActiveSharedCartExplicitByStore(storeId);
      const storedCart = storedCartId
        ? storeCarts.find((cart) => cart.id === storedCartId)
        : null;
      const hasPersonalCart = storeCarts.some((cart) => !cart?.shared_cart_id);
      const shouldIgnoreStoredShared = !!storedCart?.shared_cart_id && hasPersonalCart && !sharedExplicit;
      // Default to personal cart; shared cart should be explicit.
      const fallbackCart =
        storeCarts.find((cart) => !cart?.shared_cart_id) ||
        storeCarts.find((cart) => !!cart?.shared_cart_id) ||
        storeCarts[0];
      const selectedCart = shouldIgnoreStoredShared ? fallbackCart : (storedCart || fallbackCart);

      if (selectedCart?.id) {
        nextActiveByStore[storeId] = selectedCart.id;
        const isOwnerCollaborativeSelection = !selectedCart?.shared_cart_id && sharedExplicit;
        const isCollaborativeSelection = !!selectedCart?.shared_cart_id || isOwnerCollaborativeSelection;
        const derivedItems = selectedCart?.cart_items || [];
        const validDerivedItems = derivedItems.filter((item: any) => Number(item?.quantity || 0) > 0);
        const derivedUnits = validDerivedItems.reduce((sum: number, item: any) => sum + Number(item?.quantity || 0), 0);
        const derivedTotal = derivedItems.reduce((sum: number, item: any) => {
          const unitPrice = Number(item?.products?.fixed_sale_price ?? item?.unit_price ?? 0);
          return sum + (item.quantity && Number(item.quantity) > 0 ? unitPrice * Number(item.quantity) : 0);
        }, 0);
        const derivedSharedToken = null;
        debugActiveCartUi('derive-active', {
          storeId,
          storedCartId,
          selectedCartId: selectedCart.id,
          candidates: storeCarts.map((cart) => ({ id: cart.id, isShared: !!cart.shared_cart_id })),
        });
        setActiveCartPreference(storeId, selectedCart.id);
        setActiveCartSnapshot({
          storeId,
          cartId: selectedCart.id,
          isShared: isCollaborativeSelection,
          sharedToken: derivedSharedToken,
          units: derivedUnits,
          total: derivedTotal,
          storeName: selectedCart?.stores?.name || storeInfoMap[storeId]?.name || 'Tienda',
          storeSlug: selectedCart?.stores?.slug || storeInfoMap[storeId]?.slug,
          updatedAt: new Date().toISOString(),
        });

        if (selectedCart?.shared_cart_id) {
          setActiveSharedCartRole('guest');
          setActiveSharedCartGuestOwnerColor(sharedCartOwnerColorByCartId[selectedCart.id] || null);
        }
      }
    });

    setActiveCartIdByStore((prev) => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(nextActiveByStore);
      if (prevKeys.length === nextKeys.length && nextKeys.every((key) => prev[key] === nextActiveByStore[key])) {
        return prev;
      }
      return nextActiveByStore;
    });

    const hasSharedPriorityCart = Object.entries(nextActiveByStore).some(([storeId, activeCartId]) => {
      const cartsForStore = groupedByStore.get(storeId) || [];
      const activeCart = cartsForStore.find((cart) => cart.id === activeCartId);
      if (!activeCart) return false;
      return !!activeCart?.shared_cart_id;
    });
    setActiveSharedCartThemePriority(hasSharedPriorityCart);
    
    // Persist or clear shared cart token based on active shared cart
    setActiveSharedCartToken(null);
  }, [carts, storeInfoMap, sharedCartOwnerColorByCartId]);

  const handleSetActiveCartForStore = useCallback((cart: any, storeName?: string) => {
    const storeId = cart?.store_id;
    const cartId = cart?.id;
    if (!storeId || !cartId) return;

    const items = cart?.cart_items || [];
    const units = items.reduce((sum: number, item: any) => sum + Number(item?.quantity || 0), 0);
    const total = items.reduce((sum: number, item: any) => {
      const unitPrice = Number(item?.products?.fixed_sale_price ?? item?.unit_price ?? 0);
      return sum + (unitPrice * Number(item?.quantity || 0));
    }, 0);
    const previousSnapshot = getActiveCartSnapshot(storeId);
    const isIncomingSharedCart = !!cart?.shared_cart_id;
    const isCollaborativeSelection = isIncomingSharedCart;
    const nextSharedExplicit = isCollaborativeSelection;
    const sharedToken = null;

    debugActiveCartUi('click-set-active:start', { storeId, cartId, isShared: !!cart?.shared_cart_id, sharedToken });
    setActiveSharedCartExplicitByStore(storeId, nextSharedExplicit);
    setActiveCartPreference(storeId, cartId);
    setActiveCartSnapshot({
      storeId,
      cartId,
      isShared: isCollaborativeSelection,
      sharedToken,
      units,
      total,
      storeName: storeName || cart?.stores?.name || 'Tienda',
      storeSlug: cart?.stores?.slug || storeInfoMap[storeId]?.slug,
      updatedAt: new Date().toISOString(),
    });
    setActiveCartIdByStore((prev) => ({ ...prev, [storeId]: cartId }));
    setActiveSharedCartThemePriority(isCollaborativeSelection);

    if (cart?.shared_cart_id) {
      setActiveSharedCartRole('guest');
      setActiveSharedCartGuestOwnerColor(sharedCartOwnerColorByCartId[cartId] || null);
    }
    
    // Persist or clear shared cart token
    setActiveSharedCartToken(null);
    
    debugActiveCartUi('click-set-active:state-updated', { storeId, cartId, units, total });
    window.dispatchEvent(new CustomEvent('cart-active-changed', {
      detail: {
        storeId,
        cartId,
        isShared: isCollaborativeSelection,
      },
    }));
    window.dispatchEvent(new CustomEvent('cart-updated'));
  }, [storeInfoMap, sharedCartOwnerColorByCartId]);

  const handleCreateAndActivateNewCart = useCallback(async (storeId: string, storeName: string) => {
    if (!session?.user?.id || !storeId) {
      return;
    }

    let shouldCloseSidebarAfterReset = false;
    setCreatingNewCartStoreId(storeId);

    try {
      const cartsBefore = await getOpenCartsForUser(session.user.id);
      const sameStoreBefore = (cartsBefore || []).filter((c: any) => c?.store_id === storeId);

      // Force-create a new non-shared cart for this store (do not reuse existing).
      const newCart = await createNewCartForStore(session.user.id, storeId);

      if (!newCart) {
        toast({ description: 'Error al crear el canasto', variant: 'destructive' });
        return;
      }

      // Make the newly created personal cart active for this store.
      setActiveSharedCartExplicitByStore(storeId, false);
      setActiveSharedCartToken(null); // Clear shared token since we're creating a personal cart
      setActiveCartPreference(storeId, newCart.id);
      setActiveCartIdByStore((prev) => ({ ...prev, [storeId]: newCart.id }));
      // Reload carts to get the newly created cart with full details
      await loadCarts();
      const cartsAfter = await getOpenCartsForUser(session.user.id);
      const sameStoreAfter = (cartsAfter || []).filter((c: any) => c?.store_id === storeId);
      // Disparar evento global para refrescar el feed/tienda
      window.dispatchEvent(new CustomEvent('cart-updated', { detail: { storeId } }));
      shouldCloseSidebarAfterReset = true;
    } catch (error) {
      toast({ description: 'Error al crear el canasto', variant: 'destructive' });
    } finally {
      setCreatingNewCartStoreId((prev) => (prev === storeId ? null : prev));
      if (shouldCloseSidebarAfterReset) {
        // Give the button one short beat to return to normal color before closing.
        setTimeout(() => {
          setIsClosing(true);
          setTimeout(() => {
            closeSidebar();
            setIsClosing(false);
          }, 200);
        }, 120);
      }
    }
  }, [session?.user?.id, toast, loadCarts, closeSidebar]);

  // Consolidated realtime updates for owner carts, shared carts, and membership changes.
  useEffect(() => {
    if (!sidebarOpen || !session?.user?.id) return;

    let disposed = false;
    let activeChannel: any = null;

    const setupRealtimeSubscriptions = async () => {
      try {
        const ownerCartIds = (carts || [])
          .filter((c: any) => !c?.shared_cart_id && c?.id && c.id !== 'temp-cart' && !String(c.id).startsWith('shared-cart-'))
          .map((c: any) => c.id);

        const visibleSharedCartIds = (carts || [])
          .map((cart: any) => cart?.shared_cart_id)
          .filter(Boolean);

        const { data: memberCartRows, error: memberCartError } = await supabase
          .from('cart_access')
          .select('cart_id')
          .eq('user_id', session.user.id)
          .eq('access_type', 'shared_with')
          .then((result) => ({
            data: (result.data || []).map((row: any) => row.cart_id),
            error: result.error,
          }));

        if (memberCartError) {
          throw memberCartError;
        }

        if (disposed) return;

        const watchedCartIds = Array.from(
          new Set([...(ownerCartIds || []), ...(visibleSharedCartIds || []), ...((memberCartRows as string[]) || [])])
        );

        const channel = supabase
          .channel(`checkout-carts-live-${session.user.id}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'carts',
              filter: `user_id=eq.${session.user.id}`,
            },
            () => queueLiveCartReload()
          )
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'cart_access',
              filter: `user_id=eq.${session.user.id}`,
            },
            () => queueLiveCartReload()
          );

        watchedCartIds.forEach((cartId: string) => {
          channel.on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'cart_items',
              filter: `cart_id=eq.${cartId}`,
            },
            () => queueLiveCartReload()
          );
          channel.on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'carts',
              filter: `id=eq.${cartId}`,
            },
            () => queueLiveCartReload()
          );
        });

        channel.subscribe();
        activeChannel = channel;
      } catch (error) {
        console.error('[CheckoutSidebar] Error setting up realtime subscriptions:', error);
      }
    };

    void setupRealtimeSubscriptions();

    return () => {
      disposed = true;
      if (sharedCartRealtimeReloadRef.current) {
        clearTimeout(sharedCartRealtimeReloadRef.current);
        sharedCartRealtimeReloadRef.current = null;
      }
      if (activeChannel) {
        void supabase.removeChannel(activeChannel);
      }
    };
  }, [sidebarOpen, session?.user?.id, carts, queueLiveCartReload]);

  // Fallback polling in case realtime events are delayed/missed.
  useEffect(() => {
    if (!sidebarOpen) return;
    const interval = setInterval(() => {
      queueLiveCartReload();
    }, 1500);
    return () => clearInterval(interval);
  }, [sidebarOpen, queueLiveCartReload]);

  async function loadOrders() {
    const runId = ++loadOrdersRunRef.current;
    debugCheckoutOrders('loadOrders:start', { runId, userId: session?.user?.id || null, sidebarOpen });
    if (!session?.user?.id) {
      debugCheckoutOrders('loadOrders:noSession', { runId });
      setOrders([]);
      setLoadingOrders(false);
      return;
    }

    setLoadingOrders(true);
    try {
      const previousOrders = await getOrdersByUserId(session.user.id);
      debugCheckoutOrders('loadOrders:resolved', { runId, count: previousOrders?.length || 0 });
      setOrders(previousOrders || []);
    } catch (e) {
      debugCheckoutOrders('loadOrders:error', { runId, error: e });
      setOrders([]);
    } finally {
      debugCheckoutOrders('loadOrders:end', { runId, nextLoading: false });
      setLoadingOrders(false);
    }
  }

  async function confirmPendingOrderCancel() {
    if (!pendingOrderCancel?.id) return;

    const orderToCancel = pendingOrderCancel;
    setPendingOrderCancel(null);

    try {
      await updateOrderStatus(orderToCancel.id, 'cancelled');
      toast({
        title: 'Pedido cancelado',
        description: `Se canceló el pedido #${orderToCancel.order_number || orderToCancel.id.slice(0, 8)} antes de convocar repartidor.`,
      });
      await loadOrders();
    } catch (error) {
      console.error('[CheckoutSidebar] Error cancelling in-progress order:', error);
      toast({
        title: 'Error',
        description: `No pudimos cancelar el pedido. ${toErrorMessage(error)}`,
        variant: 'destructive',
      });
    }
  }

  // Load carts on sidebar open
  useEffect(() => {
    if (!sidebarOpen) {
      loadedForCurrentOpenRef.current = false;
      lastLoadedSessionIdRef.current = null;
      setVisibleOrdersCount(ORDERS_PAGE_SIZE);
      setShowOrdersHistory(false);
      setOrdersHistoryLoaded(false);
      debugCheckoutOrders('effect:sidebarOpen:false');
      return;
    }

    const currentSessionId = session?.user?.id || null;
    if (loadedForCurrentOpenRef.current && lastLoadedSessionIdRef.current === currentSessionId) {
      debugCheckoutOrders('effect:sidebarOpen:true -> skip duplicate load');
      return;
    }

    loadedForCurrentOpenRef.current = true;
    lastLoadedSessionIdRef.current = currentSessionId;
    setVisibleOrdersCount(ORDERS_PAGE_SIZE);
    debugCheckoutOrders('effect:sidebarOpen:true -> trigger loads (once per open)');
    loadCarts();
    loadOrders().then(() => setOrdersHistoryLoaded(true));
    recalculate();
    const timeouts = [
      setTimeout(recalculate, 0),
      setTimeout(recalculate, 50),
      setTimeout(recalculate, 100),
    ];
    return () => timeouts.forEach(t => clearTimeout(t));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarOpen, recalculate, session?.user?.id, loadCarts]);

  // Load orders history on demand
  const handleToggleOrdersHistory = useCallback(async () => {
    if (showOrdersHistory) {
      setShowOrdersHistory(false);
      return;
    }

    setShowOrdersHistory(true);
    if (!ordersHistoryLoaded) {
      await loadOrders();
      setOrdersHistoryLoaded(true);
    }
  }, [showOrdersHistory, ordersHistoryLoaded, loadOrders]);

  // Detect if there's a local cart to sync when user logs in
  useEffect(() => {
    if (!session?.user?.id) {
      setHasLocalCart(false);
      return;
    }
    try {
      const raw = localStorage.getItem('temp-cart-minibid24hs');
      if (!raw) { setHasLocalCart(false); return; }
      const parsed = JSON.parse(raw);
      setHasLocalCart(!!(parsed.items && parsed.items.length > 0));
    } catch {
      setHasLocalCart(false);
    }
  }, [session?.user?.id]);

  useEffect(() => {
    setVisibleOrdersCount(ORDERS_PAGE_SIZE);
  }, [ordersViewMode, ordersDateFilter]);

  useScrollLock(sidebarOpen || isClosing, false, scrollAreaRef);

  // Listen for cart updates from Minibid24hsPage and reload carts in real-time
  useEffect(() => {
    const handleCartUpdated = () => {
      debugCheckoutOrders('event:cart-updated', { sidebarOpen });
      console.log('🔄 [CheckoutSidebar] Cart update event received, reloading carts...');
      // Always load carts when update event is fired, don't depend on sidebarOpen
      // This ensures data is updated even if sidebar is being opened/closed
      loadCarts();
    };
    window.addEventListener('cart-updated', handleCartUpdated);
    return () => window.removeEventListener('cart-updated', handleCartUpdated);
  }, [loadCarts]);

  useEffect(() => {
    debugCheckoutOrders('state:snapshot', {
      sidebarOpen,
      hasSession: !!session?.user?.id,
      loadingOrders,
      ordersCount: orders.length,
      cartsCount: carts.length,
    });
  }, [sidebarOpen, session?.user?.id, loadingOrders, orders.length, carts.length]);

  // Hydrate undo metadata when cart is auto-imported on login (outside this sidebar)
  useEffect(() => {
    const handleCartSyncMetadata = (event: Event) => {
      const customEvent = event as CustomEvent<{
        syncedRecords?: { id: string }[];
        backupItems?: any[];
        storeId?: string;
        isUndoing?: boolean;
      }>;
      const detail = customEvent.detail;

      if (!detail?.syncedRecords || !detail?.backupItems || !detail?.storeId) return;

      setSyncMetadata({
        syncedRecords: detail.syncedRecords,
        backupItems: detail.backupItems,
        storeId: detail.storeId,
        isUndoing: !!detail.isUndoing,
      });
      setHasLocalCart(false);
      setSyncCardDismissed(false);
    };

    window.addEventListener('cart-sync-metadata', handleCartSyncMetadata as EventListener);
    return () => window.removeEventListener('cart-sync-metadata', handleCartSyncMetadata as EventListener);
  }, []);

  // Handle update quantity
  const handleUpdateQuantity = useCallback(async (itemId: string, quantityCalculator: (current: number) => number) => {
    const cart = carts.find((c) => c.cart_items?.some((it: any) => it.id === itemId));
    if (!cart) return;
    
    const item = cart.cart_items.find((it: any) => it.id === itemId);
    if (!item) return;

    setUpdatingItemIds(prev => new Set(prev).add(itemId));
    
    try {
      const currentQuantity = Number(item.quantity || 0);
      const newQuantity = Math.max(0, quantityCalculator(currentQuantity));
      if (newQuantity === currentQuantity) return;

      if (session?.user?.id) {
        if (newQuantity <= 0) {
          await removeCartItem(itemId);
        } else {
          await updateCartItem(itemId, newQuantity);
        }
      } else {
        if (newQuantity <= 0) {
          removeFromTempCart(item.product_id);
        } else {
          updateTempCartItemQuantity(item.product_id, newQuantity);
        }
      }

      await loadCarts();
      window.dispatchEvent(new CustomEvent('cart-updated'));
    } catch (e) {
      console.error('[CheckoutSidebar] Error updating item quantity:', e);
      toast({ title: 'Error', description: (e as any)?.message || 'No pudimos actualizar la cantidad', variant: 'destructive' });
    } finally {
      setUpdatingItemIds(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  }, [carts, loadCarts, session?.user?.id, toast]);

  // Handle remove item
  const handleRemoveItem = useCallback(async (itemId: string) => {
    try {
      const cart = carts.find((c) => c.cart_items?.some((it: any) => it.id === itemId));
      const item = cart?.cart_items?.find((it: any) => it.id === itemId);
      if (session?.user?.id) {
        await removeCartItem(itemId);
      } else {
        if (item) removeFromTempCart(item.product_id);
      }

      await loadCarts();
      window.dispatchEvent(new CustomEvent('cart-updated'));
    } catch (e) {
      toast({ title: 'Error', description: (e as any)?.message || 'No pudimos remover el producto', variant: 'destructive' });
    }
  }, [carts, session?.user?.id, loadCarts, toast]);

  // Handle clear cart (vaciar pedido)
  const handleClearCart = useCallback(async (cartId: string, storeId: string, options?: { finalizeCollaborative?: boolean }) => {
    try {
      // Play decrease sound when clearing cart
      playBeep('decrease');
      vibrate('decrease');

      if (session?.user?.id) {
        await clearCart(session.user.id, storeId);
      } else {
        clearTempCart();
      }
      await loadCarts();
      window.dispatchEvent(new CustomEvent('cart-updated'));
    } catch (e) {
      toast({ title: 'Error', description: (e as any)?.message || 'No pudimos vaciar el pedido', variant: 'destructive' });
    }
  }, [session?.user?.id, loadCarts, toast, playBeep, vibrate]);

  const getTourItemsFromCart = useCallback((cart: any) => {
    return (cart?.cart_items || []).filter((item: any) => item?.products?.product_type === 'tours');
  }, []);

  const calculateCartTotalWithTourQuantity = useCallback((cart: any, tourQuantityOverride?: number) => {
    return (cart?.cart_items || []).reduce((sum: number, item: any) => {
      const unitPrice = Number(item?.products?.fixed_sale_price ?? item?.unit_price ?? 0);
      const isTour = item?.products?.product_type === 'tours';
      const qty = isTour && typeof tourQuantityOverride === 'number'
        ? tourQuantityOverride
        : Number(item?.quantity || 1);
      return sum + (unitPrice * qty);
    }, 0);
  }, []);

  const openPaymentForCart = useCallback((cart: any, totalOverride?: number) => {
    const storeInfo = storeInfoMap[cart.store_id];
    const total = typeof totalOverride === 'number' ? totalOverride : calculateCartTotalWithTourQuantity(cart);

    // Collect rental metadata so MainLayout can call the RPC after order creation
    const storeRentalItems = (cart?.cart_items || []).filter((item: any) => isStoreRentalItem(item));
    let rentalMetadata: { mode: 'nueva' | 'renovar'; storeId: string | null; paidDays: number } | null = null;
    if (storeRentalItems.length > 0) {
      const mode = rentalModeByCartId[cart.id] as 'nueva' | 'renovar';
      const rentalDates = rentalDatesByCartId[cart.id];
      const qty = Math.max(
        (cart?.cart_items || []).reduce((s: number, it: any) => s + (getDailyPrice(it) !== null ? Number(it.quantity || 1) : 0), 0),
        1
      );
      const paidDays = rentalDates ? calcRentalDays(rentalDates.startDate, rentalDates.endDate) : qty;
      rentalMetadata = {
        mode,
        storeId: mode === 'renovar' ? (rentalStoreByCartId[cart.id] ?? null) : null,
        paidDays: Math.max(paidDays, 1),
      };
      console.log('[StoreRental] openPaymentForCart → rentalMetadata', JSON.stringify(rentalMetadata));
      console.log('[StoreRental] storeRentalItems count:', storeRentalItems.length, 'mode:', mode, 'rentalDates:', rentalDates, 'paidDays:', rentalMetadata.paidDays);
    } else {
      console.log('[StoreRental] openPaymentForCart → no storeRentalItems found in cart', cart.id, 'items:', cart?.cart_items?.map((i: any) => ({ title: i?.products?.title, is_store_rental: i?.products?.is_store_rental })));
    }

    setIsClosing(true);
    setTimeout(() => {
      closeSidebar();
      setIsClosing(false);
    }, 200);

    setTimeout(() => {
      openPaymentSidebar({
        storeId: cart.store_id,
        storeName: storeInfo?.name || 'Tienda',
        total,
        cartId: cart.id,
        sharedToken: null,
        isDigital: false,
        rentalMetadata,
      });
    }, 300);
  }, [storeInfoMap, calculateCartTotalWithTourQuantity, closeSidebar, openPaymentSidebar, rentalModeByCartId, rentalStoreByCartId, rentalDatesByCartId]);

  const handlePayClick = useCallback(async (cart: any) => {
    if (!session?.user?.id) {
      openUserMenu();
      return;
    }

    // Validate store rental selection
    const storeRentalItems = (cart?.cart_items || []).filter((item: any) => isStoreRentalItem(item));
    if (storeRentalItems.length > 0) {
      const mode = rentalModeByCartId[cart.id];
      if (!mode) {
        setRentalModeValidationByCartId((prev) => ({ ...prev, [cart.id]: true }));
        return;
      }
      if (mode === 'renovar' && !rentalStoreByCartId[cart.id]) {
        toast({ title: 'Falta seleccionar tienda', description: 'Seleccioná la tienda que querés renovar.', variant: 'destructive' });
        return;
      }
      // Validate rental dates
      const rentalDates = rentalDatesByCartId[cart.id];
      if (!rentalDates?.startDate || !rentalDates?.endDate) {
        toast({ title: 'Faltan fechas', description: 'Seleccioná el período de alquiler.', variant: 'destructive' });
        return;
      }
      if (calcRentalDays(rentalDates.startDate, rentalDates.endDate) < 1) {
        toast({ title: 'Fechas inválidas', description: 'La fecha de fin debe ser igual o posterior a la fecha de inicio.', variant: 'destructive' });
        return;
      }
    }

    const tourItems = getTourItemsFromCart(cart);
    if (tourItems.length > 0) {
      // Check if passenger data is already complete
      const passengerMetaFromItems = getPassengerMetaFromCartItems(cart.cart_items || []);
      const requiredPassengers = Math.max(1, ...tourItems.map((item: any) => Number(item.quantity || 1)));
      const hasPassengers = Array.isArray(passengerMetaFromItems?.passengers) && passengerMetaFromItems.passengers.length >= requiredPassengers;
      const hasItinerary = Array.isArray(passengerMetaFromItems?.itinerary) && passengerMetaFromItems.itinerary.length > 0;
      const passengerDataIsComplete = hasPassengers && hasItinerary;

      // If passenger data is complete, proceed directly to payment
      if (passengerDataIsComplete) {
        console.log('✅ [CheckoutSidebar] Passenger data is complete, proceeding to payment');
        openPaymentForCart(cart);
        return;
      }

      // Otherwise, show the passenger wall to collect missing data
      console.log('⚠️ [CheckoutSidebar] Passenger data incomplete, showing passenger wall');
      setPassengerWallLoading(true);
      setPendingPaymentCart(cart);
      setWallInitialPassengerCount(Math.max(1, ...tourItems.map((item: any) => Number(item.quantity || 1))));

      try {
        const tours = await Promise.all(
          tourItems.map(async (item: any) => {
            const fullProduct = await getProductById(item.product_id);
            return {
              id: item.product_id,
              title: fullProduct?.title || item?.products?.title || 'Excursión',
              fixed_sale_price: Number(fullProduct?.fixed_sale_price ?? item?.products?.fixed_sale_price ?? item?.unit_price ?? 0),
              store_id: cart.store_id,
              departure_time: (fullProduct as any)?.departure_time ?? null,
              latitude: (fullProduct as any)?.latitude ?? null,
              longitude: (fullProduct as any)?.longitude ?? null,
            };
          })
        );

        setWallSelectedTours(tours);
        setShowPassengerWall(true);
      } catch (error) {
        console.error('[CheckoutSidebar] Error preparing passenger details wall:', error);
        setWallSelectedTours(tourItems.map((item: any) => ({
          id: item.product_id,
          title: item?.products?.title || 'Excursión',
          fixed_sale_price: Number(item?.products?.fixed_sale_price ?? item?.unit_price ?? 0),
          store_id: cart.store_id,
        })));
        setShowPassengerWall(true);
      } finally {
        setPassengerWallLoading(false);
      }

      return;
    }

    openPaymentForCart(cart);
  }, [session?.user?.id, getTourItemsFromCart, openPaymentForCart, openUserMenu, rentalModeByCartId, rentalStoreByCartId, rentalDatesByCartId, toast]);

  const handleEditPassengers = useCallback(async (cart: any, passengerMeta: any) => {
    if (!session?.user?.id) return;

    const tourItems = getTourItemsFromCart(cart);
    if (tourItems.length === 0) return;

    const initialPassengers: Passenger[] = Array.isArray(passengerMeta?.passengers)
      ? passengerMeta.passengers.map((p: any) => ({
          id: p.id || crypto.randomUUID(),
          name: p.name || '',
          documentNumber: p.documentNumber || '',
          nationality: p.nationality || '',
        }))
      : [];

    const initialTourDays: any[] = Array.isArray(passengerMeta?.itinerary)
      ? passengerMeta.itinerary.map((it: any) => ({
          date: it.date,
          tour: { id: it.tourId, title: it.tourTitle },
          destination: it.destination ? { displayName: it.destination, coords: { lat: 0, lng: 0 } } : undefined,
        }))
      : [];

    setWallInitialPassengers(initialPassengers);
    setWallInitialTourDays(initialTourDays);
    setPassengerWallLoading(true);
    setPendingPaymentCart(cart);
    setWallInitialPassengerCount(Math.max(1, initialPassengers.length, ...tourItems.map((item: any) => Number(item.quantity || 1))));

    try {
      const tours = await Promise.all(
        tourItems.map(async (item: any) => {
          const fullProduct = await getProductById(item.product_id);
          return {
            id: item.product_id,
            title: fullProduct?.title || item?.products?.title || 'Excursión',
            fixed_sale_price: Number(fullProduct?.fixed_sale_price ?? item?.products?.fixed_sale_price ?? item?.unit_price ?? 0),
            store_id: cart.store_id,
            departure_time: (fullProduct as any)?.departure_time ?? null,
            latitude: (fullProduct as any)?.latitude ?? null,
            longitude: (fullProduct as any)?.longitude ?? null,
          };
        })
      );
      setWallSelectedTours(tours);
      setShowPassengerWall(true);
    } catch (error) {
      console.error('[CheckoutSidebar] Error loading tours for edit:', error);
      setWallSelectedTours(tourItems.map((item: any) => ({
        id: item.product_id,
        title: item?.products?.title || 'Excursión',
        fixed_sale_price: Number(item?.products?.fixed_sale_price ?? item?.unit_price ?? 0),
        store_id: cart.store_id,
      })));
      setShowPassengerWall(true);
    } finally {
      setPassengerWallLoading(false);
    }
  }, [session?.user?.id, getTourItemsFromCart]);

  // Handle sync
  const handleUndoSync = async () => {
    if (!syncMetadata || syncMetadata.isUndoing) return;
    setSyncMetadata(prev => prev ? { ...prev, isUndoing: true } : null);
    try {
      await undoSyncCart(syncMetadata.syncedRecords);
      saveTempCart({
        store_id: syncMetadata.storeId,
        items: syncMetadata.backupItems,
        created_at: new Date().toISOString()
      });
      setSyncMetadata(null);
      toast({
        title: '🔄 Importación deshecha',
        description: 'Los productos han vuelto a tu memoria local.',
      });
      window.dispatchEvent(new Event('cart-updated'));
      window.dispatchEvent(new Event('cart-synced'));
    } catch (err) {
      console.error('[CheckoutSidebar] Error undoing sync:', err);
      setSyncMetadata(prev => prev ? { ...prev, isUndoing: false } : null);
    }
  };

  const handleSyncLocalCart = useCallback(async () => {
    setIsSyncingCart(true);
    try {
      const result = await syncLocalCartToDB();
      if (result.success && result.count && result.count > 0 && result.syncedRecords && result.backupItems && result.storeId) {
        setSyncMetadata({
          syncedRecords: result.syncedRecords,
          backupItems: result.backupItems,
          storeId: result.storeId,
          isUndoing: false
        });
        setHasLocalCart(false);
        setSyncCardDismissed(false);
        toast({ title: '✅ Canasto importado', description: result.message });
        window.dispatchEvent(new Event('cart-synced'));
        // Forzar apertura de la sidebar del checkout
        setTimeout(() => {
          openSidebar({ cartOnly: true });
        }, 200);
      } else {
        toast({ title: 'Error', description: result.message, variant: 'destructive' });
      }
    } catch (err) {
      console.error('[CheckoutSidebar] Error syncing cart:', err);
      toast({ title: 'Error', description: 'No pudimos sincronizar tu canasto', variant: 'destructive' });
    } finally {
      setIsSyncingCart(false);
    }
  }, [syncLocalCartToDB, toast]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      closeSidebar();
      setIsClosing(false);
    }, 200);
  }, [closeSidebar]);

  // Pull-to-close
  usePullToClose(scrollAreaRef, {
    onClose: handleClose,
    threshold: 80,
    enabled: sidebarOpen && !isDesktop && !modalOpen
  });

  // Countdown to clear cart: decrement every second, clear at 0
  useEffect(() => {
    if (!clearCountdown || clearCountdown.count <= 0) return;
    const timer = setTimeout(() => {
      setClearCountdown((prev) => (prev ? { ...prev, count: prev.count - 1 } : null));
    }, 1000);
    return () => clearTimeout(timer);
  }, [clearCountdown]);

  useEffect(() => {
    if (clearCountdown?.count === 0) {
      const { cartId, storeId } = clearCountdown;
      setClearCountdown(null);
      void handleClearCart(cartId, storeId, { finalizeCollaborative: false });
    }
  }, [clearCountdown?.count]);

  // Cancel countdown when sidebar closes
  useEffect(() => {
    if (!sidebarOpen && clearCountdown) {
      setClearCountdown(null);
    }
  }, [sidebarOpen]);

  // Click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clearCountdown !== null) return; // No cerrar sidebar si hay un countdown activo
      const target = event.target as HTMLElement;
      const isPortal = target.closest('[data-radix-portal]') || 
                      target.closest('[data-radix-popper-content-wrapper]') ||
                      target.closest('[role="dialog"]') ||
                      target.closest('[role="menu"]') || 
                      target.closest('[role="listbox"]') ||
                      target.closest('.leaflet-container') ||
                      target.closest('.pac-container');
      // Don't close if the ProductSidebar is open on top
      const isProductSidebarOpen = !!document.querySelector('[data-product-sidebar-scroll]');
      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(target) && !isPortal && !isProductSidebarOpen) {
        handleClose();
      }
    };
    if (sidebarOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen, handleClose, clearCountdown]);

  // ESC key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && sidebarOpen && !modalOpen && !document.querySelector('[data-product-sidebar-scroll]')) {
        event.preventDefault();
        handleClose();
      }
    };
    if (sidebarOpen) document.addEventListener('keydown', handleKeyDown, false);
    return () => document.removeEventListener('keydown', handleKeyDown, false);
  }, [sidebarOpen, handleClose, modalOpen]);

  return (
    <>
    <AnimatePresence>
      {(sidebarOpen || isClosing) && (
        <>
          {/* Overlay - igual que FavoritesSidebar, solo cierra si el click es sobre el overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-transparent z-[9999]"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                e.stopPropagation();
                handleClose();
              }
            }}
            onTouchStart={(e) => {
              if (e.target === e.currentTarget) {
                e.stopPropagation();
                handleClose();
              }
            }}
          />

          {/* Sidebar visual: igual estructura y clases que FavoritesSidebar */}
          <motion.div
            ref={sidebarRef}
            onMouseEnter={cancelCloseDelay}
            onMouseLeave={() => { if (isDesktop && sidebarOpen) closeWithDelay(1000); }}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.2 }}
            className={cn(
              'fixed right-0 w-full max-w-[100vw] lg:w-[400px] bg-background shadow-2xl z-[10000] flex flex-col overflow-x-hidden'
            )}
            style={{
              top: isDesktop ? `${headerHeight}px` : 0,
              bottom: !isDesktop ? 0 : undefined,
              height: isDesktop ? `calc(100vh - ${headerHeight}px)` : '100dvh',
              pointerEvents: (sidebarOpen && !isClosing && !modalOpen) ? 'auto' : 'none'
            }}
          >
            {/* Safe Area Spacer for Mobile */}
            {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}

            {/* Header visual igual a FavoritesSidebar, pero con ícono de ReceiptText */}
            <div className="flex items-center justify-between gap-2 p-4 border-b shrink-0 bg-primary text-primary-foreground min-h-[64px]">
              <h2 className="text-lg font-semibold flex min-w-0 items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-primary-foreground" />
                <span className="truncate">Tus Pedidos</span>
              </h2>
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleClose(); }}
                        className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
                      >
                        <X className="w-5 h-5 text-white" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent variant="sidebar">
                      <p>Cerrar</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {showPassengerWall && pendingPaymentCart && (
              <div className="absolute inset-0 z-30 bg-background overflow-x-hidden">
                <PassengerDetailsSidebar
                  isOpen={showPassengerWall}
                  onClose={() => {
                    setShowPassengerWall(false);
                    setPendingPaymentCart(null);
                    setWallInitialPassengers([]);
                    setWallInitialTourDays([]);
                  }}
                  quantity={wallInitialPassengerCount}
                  selectedTours={wallSelectedTours}
                  initialPassengers={wallInitialPassengers.length > 0 ? wallInitialPassengers : undefined}
                  initialTourDays={wallInitialTourDays.length > 0 ? wallInitialTourDays : undefined}
                  proceedLabel="Guardar datos de pasajeros"
                  closeLabel="Volver al canasto"
                  loading={passengerWallLoading}
                  onProceedToCheckout={async (passengers, tourDays, totalPrice, pickup) => {
                    if (!session?.user?.id || !pendingPaymentCart) return;

                    setPassengerWallLoading(true);
                    try {
                      const tourItems = getTourItemsFromCart(pendingPaymentCart);
                      const passengerCount = Math.max(1, passengers.length);

                      await Promise.all(
                        tourItems.map((item: any) => updateCartItem(item.id, passengerCount))
                      );

                      // Save passenger details to cart_items.details (primary storage)
                      const itemDetails = {
                        passengers: passengers.map(p => ({
                          id: p.id,
                          name: p.name,
                          documentNumber: p.documentNumber,
                          nationality: p.nationality,
                        })),
                        tourDays: tourDays.map(d => ({
                          date: d.date,
                          tourId: d.tour.id,
                          tourTitle: d.tour.title,
                          destination: d.destination?.displayName,
                        })),
                        pickupLocation: pickup ? {
                          displayName: pickup.displayName,
                          lat: pickup.coords?.lat,
                          lng: pickup.coords?.lng,
                          floor: pickup.floor,
                          apartment: pickup.apartment,
                        } : undefined,
                        totalPrice,
                        passengerCount,
                        updatedAt: new Date().toISOString(),
                      };

                      console.log('💾 [CheckoutSidebar] Saving passenger details to cart_items:', itemDetails);
                      await updateCartItemsDetailsForStore(session.user.id, pendingPaymentCart.store_id, itemDetails);
                      console.log('✅ [CheckoutSidebar] Passenger details saved successfully');

                      await loadCarts();
                      window.dispatchEvent(new CustomEvent('cart-updated'));

                      setShowPassengerWall(false);
                      setPendingPaymentCart(null);
                    } catch (error) {
                      console.error('[CheckoutSidebar] Error confirming passenger details:', error);
                      toast({
                        title: 'Error',
                        description: 'No pudimos guardar los datos de pasajeros para continuar al pago',
                        variant: 'destructive',
                      });
                    } finally {
                      setPassengerWallLoading(false);
                    }
                  }}
                />
              </div>
            )}

            {/* Content visual: padding y espaciado igual que FavoritesSidebar */}
            <ScrollArea ref={scrollAreaRef} className="flex-1 overflow-x-hidden" onClick={e => e.stopPropagation()}>
              <div
                className="p-4 pr-6 space-y-6 min-w-0 max-w-full overflow-x-hidden"
                style={{ paddingBottom: isDesktop ? '1rem' : 'env(safe-area-inset-bottom, 0px)' }}
                onClick={e => e.stopPropagation()}
              >
                {/* Sync Prompt Card - shown when logged in and has local cart */}
                {session?.user?.id && hasLocalCart && !syncCardDismissed && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="p-3 mb-4 bg-primary/5 border border-primary/20 rounded-xl flex items-start gap-3 shadow-sm max-w-full min-w-0">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <ShoppingCart className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 min-w-0">
                          <p className="text-sm font-semibold text-primary mb-0 truncate">¿Importar canasto guardado?</p>
                          <button
                            onClick={() => setSyncCardDismissed(true)}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            aria-label="Cerrar"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Tenés productos en el canasto que agregaste sin iniciar sesión. ¿Querés importarlos a tu cuenta?
                        </p>
                        <div className="flex gap-2 mt-3 flex-wrap">
                          <Button
                            onClick={handleSyncLocalCart}
                            disabled={isSyncingCart}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground h-8 px-4 rounded-full text-xs font-bold shadow-sm shadow-primary/20"
                          >
                            {isSyncingCart ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Importando...
                              </>
                            ) : (
                              'Sí, importar'
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              localStorage.removeItem('temp-cart-minibid24hs');
                              setHasLocalCart(false);
                              setSyncCardDismissed(true);
                            }}
                            disabled={isSyncingCart}
                            className="h-8 px-4 rounded-full text-xs font-medium text-muted-foreground hover:bg-muted"
                          >
                            Descartar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Undo sync card */}
                <AnimatePresence>
                  {syncMetadata && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-3 mb-4 bg-primary/5 border border-primary/20 rounded-xl flex items-start gap-3 shadow-sm max-w-full overflow-hidden min-w-0">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <ShoppingCart className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-primary [overflow-wrap:anywhere]">Productos importados</p>
                          <p className="text-xs text-muted-foreground mt-0.5 [overflow-wrap:anywhere]">
                            Hemos traído los productos que tenías antes de iniciar sesión.
                          </p>
                          <button
                            onClick={handleUndoSync}
                            disabled={syncMetadata.isUndoing}
                            className="mt-2 inline-flex max-w-full items-center gap-1.5 text-xs font-bold text-primary hover:text-primary/70 transition-colors disabled:opacity-50"
                          >
                            <RotateCcw className={cn("w-3 h-3 shrink-0", syncMetadata.isUndoing && "animate-spin")} />
                            <span className="text-left whitespace-normal [overflow-wrap:anywhere]">
                              {syncMetadata.isUndoing ? 'Deshaciendo...' : 'Deshacer importación'}
                            </span>
                          </button>
                        </div>
                        <button 
                          onClick={() => setSyncMetadata(null)}
                          className="text-muted-foreground hover:text-foreground shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Carts - Store headers + product grid (FavoritesSidebar-like) */}
                {carts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <ShoppingCart className="h-9 w-9 text-muted-foreground/30 mb-2" />
                  </div>
                ) : (
                  orderedCartsForDisplay.map((cart) => {
                    const storeInfo = storeInfoMap[cart.store_id] || { name: 'Tienda', id: cart.store_id };
                    const itemCount = cart.cart_items?.length || 0;
                    const unitCount = cart.cart_items?.reduce((sum: number, item: any) => {
                      return sum + Number(item?.quantity || 1);
                    }, 0) || 0;
                    const cartTotal = cart.cart_items?.reduce((sum: number, item: any) => {
                      const unitPrice = Number(item?.products?.fixed_sale_price ?? item?.unit_price ?? 0);
                      return sum + (unitPrice * Number(item?.quantity || 1));
                    }, 0) || 0;
                    const tourItems = getTourItemsFromCart(cart);
                    const hasTourItems = tourItems.length > 0;
                    const requiredPassengers = hasTourItems
                      ? Math.max(1, ...tourItems.map((item: any) => Number(item.quantity || 1)))
                      : 0;
                    
                    // Get passenger meta exclusively from cart_items.details
                    const passengerMetaFromItems = getPassengerMetaFromCartItems(cart.cart_items || []);
                    const passengerMeta = passengerMetaFromItems;
                    
                    if (passengerMetaFromItems) {
                      console.log('✅ [CheckoutSidebar] Using passenger meta from cart_items.details');
                    } else {
                      console.log('⚠️ [CheckoutSidebar] No passenger meta found in cart_items.details');
                    }
                    
                    const hasPassengers = Array.isArray(passengerMeta?.passengers) && passengerMeta.passengers.length >= requiredPassengers;
                    const hasItinerary = Array.isArray(passengerMeta?.itinerary) && passengerMeta.itinerary.length > 0;
                    const passengerDataCompleted = hasTourItems ? (hasPassengers && hasItinerary) : true;
                    const passengerEntries = hasPassengers
                      ? passengerMeta.passengers
                          .map((p: any) => {
                            const name = (p?.name || '').trim();
                            const doc = (p?.documentNumber || '').trim();
                            if (!name && !doc) return null;
                            if (name && doc) return `${name} · ID ${doc}`;
                            return name || `ID ${doc}`;
                          })
                          .filter(Boolean)
                      : [];
                    const itineraryEntries = hasItinerary
                      ? passengerMeta.itinerary.filter((it: any) => it.date)
                      : [];
                    const storeSlug = 'slug' in storeInfo ? storeInfo.slug : undefined;
                    const sharedOwnerName = sharedCartOwnerNameByCartId[cart.id];
                    const sharedActivityLogs = sharedActivityByCartId[cart.id] || [];
                    const existingSharedMembers = sharedMembersByCartId[cart.id] || [];
                    const isIncomingSharedCart = !!cart?.shared_cart_id;
                    const ownerHasCollaborators = existingSharedMembers.length > 0 && !isIncomingSharedCart;
                    const hasExternalCollaboratorActivity = ownerHasCollaborators
                      && sharedActivityLogs.some((log) => !!log?.actor_user_id && log.actor_user_id !== session?.user?.id);
                    const isOwnerSharedCart = ownerHasCollaborators || hasExternalCollaboratorActivity;
                    const isSharedCollabCart = isIncomingSharedCart || isOwnerSharedCart;
                    const cartsInSameStore = carts.filter((c) => c.store_id === cart.store_id);
                    const hasSwitchableCartsInStore = cartsInSameStore.length > 1;
                    const isActiveCart = cartsInSameStore.length === 1
                      ? true
                      : (activeCartIdByStore[cart.store_id]
                        ? activeCartIdByStore[cart.store_id] === cart.id
                        : cartsInSameStore[0]?.id === cart.id);
                    const isCreatingNewCart = creatingNewCartStoreId === cart.store_id;
                    const visibleShareRecipients = (shareUserSearchTerm.trim()
                      ? platformSearchRecipients
                      : shareRecipients
                    ).filter((recipient) => recipient.id !== session?.user?.id);
                    const existingSharedMemberIds = new Set(existingSharedMembers.map((member) => member.id));
                    const selectableShareRecipients = visibleShareRecipients.filter((recipient) => !existingSharedMemberIds.has(recipient.id));
                    const passengerChipText = requiredPassengers === 1
                      ? 'Falta completar datos del pasajero'
                      : 'Falta completar datos de los pasajeros';

                    const getActivityActorName = (actorUserId: string | null | undefined) => {
                      if (!actorUserId) return 'Alguien';
                      if (actorUserId === session?.user?.id) return 'Vos';
                      return sharedActivityActorNameByUserId[actorUserId] || 'Alguien';
                    };

                    const getActivityMessage = (log: SharedCartActivityLog) => {
                      const actorName = getActivityActorName(log.actor_user_id);
                      const isYou = actorName === 'Vos';
                      const productTitle = log.product_title || 'producto';
                      const quantityAfter = Number(log.quantity_after || 0);
                      const quantityBefore = Number(log.quantity_before || 0);

                      if (log.action === 'added') {
                        return isYou
                          ? `Agregaste ${quantityAfter}x ${productTitle}`
                          : `${actorName} agregó ${quantityAfter}x ${productTitle}`;
                      }

                      if (log.action === 'removed') {
                        return isYou
                          ? `Quitaste ${productTitle}`
                          : `${actorName} quitó ${productTitle}`;
                      }

                      if (log.action === 'cleared') {
                        return isYou
                          ? 'Vaciaste el canasto'
                          : `${actorName} vació el canasto`;
                      }

                      return isYou
                        ? `Actualizaste ${productTitle} (${quantityBefore} -> ${quantityAfter})`
                        : `${actorName} actualizó ${productTitle} (${quantityBefore} -> ${quantityAfter})`;
                    };

                    return (
                      <div key={cart.id} className="space-y-4 pb-4 border-b border-border/30 last:border-b-0 min-w-0 max-w-full overflow-hidden">
                        {/* Store Header */}
                        <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg border border-border/50 min-w-0">
                          <StoreIcon className="h-4 w-4 text-primary shrink-0" />
                          <h3 className="text-sm font-semibold text-foreground truncate flex-1 min-w-0">{storeInfo.name}</h3>
                          {/* Icon buttons on the right */}
                          <div className="flex items-center gap-1.5 shrink-0">
                              <button
                                onClick={() => {
                                  if (expandedQRCartId === cart.id) {
                                    setExpandedQRCartId(null);
                                    return;
                                  }

                                  setExpandedQRCartId(cart.id);
                                }}
                                className="h-9 w-9 min-w-9 p-0 rounded-full shadow-md hover:shadow-lg transition-all active:scale-75 duration-75 bg-white/80 dark:bg-gray-900/80 hover:bg-white dark:hover:bg-gray-800 border-0 flex items-center justify-center shrink-0 aspect-square text-primary"
                                title="Ver código QR del canasto"
                              >
                                <QrCode className="h-5 w-5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!session?.user?.id) {
                                    openUserMenu();
                                    return;
                                  }

                                  const shouldOpen = expandedSharePeopleCartId !== cart.id;
                                  setExpandedSharePeopleCartId(shouldOpen ? cart.id : null);
                                  setShareUserSearchTerm('');
                                  setPlatformSearchRecipients([]);
                                  if (shouldOpen) {
                                    setSelectedShareRecipientIdsByCartId((prev) => ({ ...prev, [cart.id]: [] }));
                                    void loadShareRecipients();
                                    void loadSharedMembersForCart(cart.id);
                                  }
                                }}
                                className="h-9 w-9 min-w-9 p-0 rounded-full shadow-md hover:shadow-lg transition-all active:scale-75 duration-75 bg-white/80 dark:bg-gray-900/80 hover:bg-white dark:hover:bg-gray-800 border-0 flex items-center justify-center shrink-0 aspect-square text-primary"
                                title="Compartir canasto con usuarios"
                              >
                                <UserPlus className="h-5 w-5" />
                              </button>
                              {clearCountdown?.cartId === cart.id ? (
                                <button
                                  onClick={() => setClearCountdown(null)}
                                  className="h-9 w-9 min-w-9 p-0 rounded-full shadow-md bg-destructive border-0 flex items-center justify-center shrink-0 aspect-square text-destructive-foreground font-bold text-base"
                                  title="Click para cancelar"
                                >
                                  {clearCountdown.count}
                                </button>
                              ) : (
                                <button
                                  onClick={() => setClearCountdown({ cartId: cart.id, storeId: cart.store_id, count: 3 })}
                                  className="h-9 w-9 min-w-9 p-0 rounded-full shadow-md hover:shadow-lg transition-all active:scale-75 duration-75 bg-white/80 dark:bg-gray-900/80 hover:bg-white dark:hover:bg-gray-800 border-0 flex items-center justify-center shrink-0 aspect-square text-destructive"
                                  title="Vaciar canasto"
                                >
                                  <X className="h-5 w-5" />
                                </button>
                              )}
                          </div>
                        </div>

                        {(() => {
                          const currentStoreSlug = extractStoreSlugFromPathname(location.pathname);
                          const isCurrentStoreRoute = currentStoreSlug === storeSlug;
                          
                          return isCurrentStoreRoute ? (
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2 rounded-lg border border-primary/20 bg-primary/5 min-w-0">
                              <div className="min-w-0 w-full sm:w-auto">
                                <p className="text-xs font-semibold text-primary inline-flex items-center gap-1.5">
                                  {isActiveCart ? (
                                    <>
                                      <Check className="h-3.5 w-3.5" />
                                      <span>Canasto activo en esta tienda</span>
                                    </>
                                  ) : (
                                    'Canasto disponible en esta tienda'
                                  )}
                                </p>
                                {hasSwitchableCartsInStore && (
                                  <p className="text-[11px] text-muted-foreground [overflow-wrap:anywhere]">
                                    El boton flotante usa el canasto activo.
                                  </p>
                                )}
                              </div>
                              <div className="flex w-full sm:w-auto items-center gap-2">
                                {hasSwitchableCartsInStore && (
                                  <button
                                    onClick={() => handleSetActiveCartForStore(cart, storeInfo.name)}
                                    disabled={isActiveCart}
                                    className={cn(
                                      'h-8 px-3 rounded-full text-xs font-semibold transition-colors flex-1 sm:flex-none',
                                      isActiveCart
                                        ? 'bg-primary text-primary-foreground cursor-default'
                                        : 'bg-background border border-primary/30 text-primary hover:bg-primary/10'
                                    )}
                                  >
                                    {isActiveCart ? 'Activo' : 'Hacer activo'}
                                  </button>
                                )}

                                <button
                                  onClick={() => handleCreateAndActivateNewCart(cart.store_id, storeInfo.name)}
                                  disabled={isCreatingNewCart}
                                  className={cn(
                                    'inline-flex h-auto min-h-8 items-center justify-center gap-1.5 rounded-full px-1 py-1 text-[11px] font-normal transition-colors flex-1 sm:flex-none whitespace-normal [overflow-wrap:anywhere]',
                                    isCreatingNewCart
                                      ? 'text-muted-foreground/70 cursor-wait'
                                      : 'text-muted-foreground/70 hover:text-muted-foreground'
                                  )}
                                >
                                  {isCreatingNewCart ? (
                                    <>
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      <span>Creando...</span>
                                    </>
                                  ) : (
                                    <>
                                      <ShoppingCart className="h-3.5 w-3.5" />
                                      <span>Nuevo canasto activo</span>
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          ) : null;
                        })()}

                        {existingSharedMembers.length > 0 && (
                          <div className="flex w-full min-w-0 max-w-full items-center gap-2 px-3 py-2 rounded-lg border border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10 overflow-hidden">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <Users className="h-4 w-4 text-primary shrink-0" />
                              <p className="text-xs font-semibold text-primary truncate">
                                Compartiendo este canasto con
                              </p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0 -space-x-2 overflow-hidden">
                              {existingSharedMembers.slice(0, 3).map((member) => (
                                <Tooltip key={member.id}>
                                  <TooltipTrigger asChild>
                                    <Avatar className="h-6 w-6 shrink-0 border border-primary/30 ring-1 ring-background hover:ring-primary/50 transition-all">
                                      {member.avatar_url ? (
                                        <AvatarImage src={member.avatar_url} />
                                      ) : (
                                        <AvatarFallback className="text-[10px]">
                                          {(member.display_name || member.username || 'U')[0].toUpperCase()}
                                        </AvatarFallback>
                                      )}
                                    </Avatar>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom" className="text-xs">
                                    {member.display_name || member.username}
                                  </TooltipContent>
                                </Tooltip>
                              ))}
                              {existingSharedMembers.length > 3 && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="h-6 w-6 rounded-full bg-primary/20 text-primary border border-primary/30 flex items-center justify-center text-[10px] font-semibold shrink-0">
                                      +{existingSharedMembers.length - 3}
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom" className="text-xs max-w-xs">
                                    {existingSharedMembers.slice(3).map((m) => m.display_name || m.username).join(', ')}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        )}

                        {hasTourItems && !passengerDataCompleted && (
                          <div className="flex w-full min-w-0 max-w-full items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold border border-amber-300 overflow-hidden">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-600" />
                            <span className="min-w-0 [overflow-wrap:anywhere]">{passengerChipText}</span>
                          </div>
                        )}

                        {hasTourItems && passengerDataCompleted && (
                          <div className="flex w-full min-w-0 max-w-full items-center gap-2 flex-wrap">
                            <div className="inline-flex min-w-0 max-w-full items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs font-semibold border border-emerald-300">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                              <span className="min-w-0 [overflow-wrap:anywhere]">Datos de pasajeros completos</span>
                            </div>
                            <button
                              onClick={() => { void handleEditPassengers(cart, passengerMeta); }}
                              className="inline-flex max-w-full items-center gap-1 px-3 py-1.5 rounded-full bg-background text-xs font-semibold border border-primary/30 text-primary hover:bg-primary/5 transition-colors"
                            >
                              Editar
                            </button>
                          </div>
                        )}

                        {/* QR Code Expanded Section */}
                        <AnimatePresence>
                          {expandedQRCartId === cart.id && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              {(() => {
                                const sharedPayload = {
                                  v: 1 as const,
                                  created_at: new Date().toISOString(),
                                  store: {
                                    id: cart.store_id,
                                    name: storeInfo.name,
                                    slug: storeSlug,
                                  },
                                  items: sortCartItemsConsistently(cart.cart_items || []).map((item: any) => ({
                                    product_id: item.product_id,
                                    title: item.products?.title || 'Producto',
                                    quantity: Number(item.quantity) || 1,
                                    unit_price: Number(item.unit_price) || 0,
                                  })),
                                };
                                const qrUrl = buildSharedCheckoutUrl(sharedPayload, window.location.origin);
                                const isLoadingSharedToken = false;

                                return (
                              <div className="mt-2 p-4 bg-gradient-to-b from-primary/5 to-primary/10 border border-primary/20 rounded-xl flex flex-col items-center gap-3">
                                {isLoadingSharedToken ? (
                                  <div className="w-full py-6 flex flex-col items-center gap-2 text-muted-foreground">
                                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                    <p className="text-xs">Generando enlace colaborativo...</p>
                                  </div>
                                ) : (
                                  <div className="bg-white p-3 rounded-lg shadow-sm">
                                    <QRCodeCanvas 
                                      value={qrUrl}
                                      size={isDesktop ? 220 : 180}
                                      level="M"
                                      includeMargin={true}
                                    />
                                  </div>
                                )}
                                <div className="flex flex-col gap-2 w-full items-center">
                                  <p className="text-xs text-muted-foreground text-center">
                                    Comparte este QR para que otros colaboren en el mismo canasto
                                  </p>
                                  <Button 
                                    onClick={() => {
                                      navigator.clipboard.writeText(qrUrl).then(() => {
                                        toast({ title: '✅ Enlace copiado', description: 'URL del canasto copiada al portapapeles' });
                                      });
                                    }}
                                    size="sm"
                                    className="w-full"
                                  >
                                    Copiar enlace
                                  </Button>
                                </div>
                              </div>
                                );
                              })()}
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Add people Inline Section */}
                        <AnimatePresence>
                          {expandedSharePeopleCartId === cart.id && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="mt-2 p-3 bg-muted/30 border border-border/60 rounded-xl space-y-3">
                                <div>
                                  <p className="text-sm font-semibold">Compartir canasto</p>
                                  <p className="text-xs text-muted-foreground">
                                    Busca usuarios que sigues o cualquier usuario de la plataforma.
                                  </p>
                                </div>

                                <div className="space-y-2">
                                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                    Ya compartido con
                                  </p>
                                  {loadingSharedMembersByCartId[cart.id] ? (
                                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      Cargando colaboradores...
                                    </div>
                                  ) : existingSharedMembers.length === 0 ? (
                                    <p className="text-xs text-muted-foreground">Todavía no compartiste este canasto con nadie.</p>
                                  ) : (
                                    <div className="flex flex-wrap gap-2">
                                      {existingSharedMembers.map((member) => {
                                        const displayName = member.display_name || member.username || 'Usuario';
                                        const isPendingRemoval = pendingSharedMemberRemoval?.cartId === cart.id
                                          && pendingSharedMemberRemoval?.userId === member.id;
                                        return (
                                          <div
                                            key={member.id}
                                            className={cn(
                                              'inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs',
                                              isPendingRemoval
                                                ? 'border-amber-400 bg-amber-100/60 text-amber-900'
                                                : 'border-primary/25 bg-primary/10 text-primary'
                                            )}
                                          >
                                            <span className="truncate max-w-[10rem]">{displayName}</span>
                                            <button
                                              type="button"
                                              onClick={() => scheduleSharedMemberRemoval(cart.id, member.id, displayName)}
                                              className="rounded-full p-0.5 hover:bg-black/10 transition-colors"
                                              title="Remover colaboración"
                                            >
                                              <X className="h-3 w-3" />
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {pendingSharedMemberRemoval?.cartId === cart.id && (
                                    <div className="flex items-center justify-between gap-2 rounded-lg border border-amber-400/60 bg-amber-100/70 px-2 py-1.5 text-[11px] text-amber-900">
                                      <span className="truncate">
                                        Removiendo a {pendingSharedMemberRemoval.displayName} en {pendingSharedMemberRemoval.remaining}s
                                      </span>
                                      <button
                                        type="button"
                                        onClick={cancelPendingSharedMemberRemoval}
                                        className="underline font-semibold hover:no-underline"
                                      >
                                        Deshacer
                                      </button>
                                    </div>
                                  )}
                                </div>

                                <div className="relative">
                                  <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
                                  <Input
                                    value={shareUserSearchTerm}
                                    onChange={(e) => setShareUserSearchTerm(e.target.value)}
                                    placeholder="Buscar por nombre o usuario"
                                    className="h-8 pl-8 text-xs"
                                  />
                                </div>

                                <div className="max-h-52 overflow-auto rounded-lg border border-border/60 bg-background/70">
                                  {loadingShareRecipients || loadingPlatformSearchRecipients ? (
                                    <div className="p-3 text-xs text-muted-foreground flex items-center gap-2">
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      Cargando usuarios...
                                    </div>
                                  ) : selectableShareRecipients.length === 0 ? (
                                    <div className="p-3 text-xs text-muted-foreground">
                                      No encontramos usuarios para agregar con ese criterio.
                                    </div>
                                  ) : (
                                    <div className="divide-y divide-border/50">
                                      {selectableShareRecipients.map((recipient) => {
                                        const isSelected = (selectedShareRecipientIdsByCartId[cart.id] || []).includes(recipient.id);
                                        const displayName = recipient.display_name || recipient.username || 'Usuario';
                                        return (
                                          <button
                                            type="button"
                                            key={recipient.id}
                                            onClick={() => handleToggleShareRecipient(cart.id, recipient.id)}
                                            className={cn(
                                              'w-full px-3 py-2 flex items-center gap-2 text-left transition-colors',
                                              isSelected ? 'bg-primary/10' : 'hover:bg-muted/50'
                                            )}
                                          >
                                            <Avatar className="h-7 w-7 shrink-0">
                                              {recipient.avatar_url ? (
                                                <AvatarImage src={recipient.avatar_url} />
                                              ) : (
                                                <AvatarFallback>
                                                  {(displayName || 'U').charAt(0).toUpperCase()}
                                                </AvatarFallback>
                                              )}
                                            </Avatar>
                                            <div className="min-w-0 flex-1">
                                              <p className="text-sm truncate">{displayName}</p>
                                              {recipient.username && (
                                                <p className="text-[11px] text-muted-foreground truncate">@{recipient.username}</p>
                                              )}
                                            </div>
                                            {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>

                                <Button
                                  onClick={() => { void handleShareCartWithSelectedUsers(cart); }}
                                  disabled={sharingByCartId[cart.id] || (selectedShareRecipientIdsByCartId[cart.id] || []).length === 0}
                                  className="w-full"
                                >
                                  {sharingByCartId[cart.id] ? (
                                    <>
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      Compartiendo...
                                    </>
                                  ) : 'Compartir canasto'}
                                </Button>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {isSharedCollabCart && (
                          <div className="rounded-xl border border-primary/25 bg-primary/5 p-3">
                            <div className="flex items-center gap-2">
                              <Users className="h-5 w-5 text-primary" />
                              <ShoppingCart className="h-5 w-5 text-primary" />
                              <span className="text-sm font-semibold text-foreground">
                                {isIncomingSharedCart
                                  ? (sharedOwnerName ? `${sharedOwnerName} compartió este canasto contigo` : 'Este canasto fue compartido contigo')
                                  : 'Este es tu canasto colaborativo'}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Products Grid */}
                        <div className="grid grid-cols-1 gap-3 w-full min-w-0">
                          {sortCartItemsConsistently(cart.cart_items || []).map((item: any) => {
                            const unitPrice = Number(item?.products?.fixed_sale_price ?? item?.unit_price ?? 0);
                            const itemSubtotal = unitPrice * (item.quantity || 1);
                            const imageUrl = item.products?.main_image_url || item.products?.cover_url || (Array.isArray(item.products?.image_urls) ? item.products.image_urls[0] : null);
                            const itemTourId = String(item.product_id || item.products?.id || '').trim();
                            const itemTourTitle = String(item.products?.title || '').trim().toLowerCase();
                            const itemItineraryDates = item?.products?.product_type === 'tours'
                              ? itineraryEntries
                                  .filter((it: any) => {
                                    const itineraryTourId = String(it?.tourId || '').trim();
                                    const itineraryTourTitle = String(it?.tourTitle || '').trim().toLowerCase();
                                    if (itemTourId && itineraryTourId) return itineraryTourId === itemTourId;
                                    return itemTourTitle && itineraryTourTitle && itineraryTourTitle === itemTourTitle;
                                  })
                                  .map((it: any) => String(it.date))
                              : [];
                            return (
                              <div 
                                key={item.id}
                                className="bg-card rounded-xl border border-border/50 p-3 pb-4 flex gap-3 group hover:border-primary/30 transition-all shadow-sm hover:shadow-md min-w-0 max-w-full overflow-visible"
                              >
                                {/* Product Image */}
                                <div
                                  className="h-16 w-16 sm:h-20 sm:w-20 rounded-lg bg-primary/10 flex-shrink-0 relative overflow-hidden flex items-center justify-center cursor-pointer"
                                  onClick={() => handleOpenProduct(item.product_id || item.products?.id)}
                                >
                                  {imageUrl ? (
                                    <img src={imageUrl} alt={item.products?.title || 'Producto'} className="w-full h-full object-cover" />
                                  ) : (
                                    <ShoppingCart className="w-10 h-10 text-primary/30" />
                                  )}
                                  
                                  {/* Heart Toggle Button */}
                                  <div className="absolute top-1 right-1 z-10">
                                    <Button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const wasNotFavorited = !isFavorited(item.products?.id || item.id);
                                        toggleFavorite({
                                          id: item.products?.id || item.id,
                                          title: item.products?.title || 'Producto',
                                          description: item.products?.description || '',
                                          fixed_sale_price: Number(item?.products?.fixed_sale_price ?? item?.unit_price ?? 0),
                                          stock: item.products?.stock || 0,
                                          addedAt: Date.now(),
                                          store_id: cart.store_id,
                                          store_name: storeInfo?.name || 'Tienda'
                                        });
                                        playFavoriteSound(wasNotFavorited);
                                        vibrate(wasNotFavorited ? 'add' : 'decrease');
                                      }}
                                      size="sm"
                                      variant="ghost"
                                      className="h-10 w-10 p-0 rounded-full shadow-md hover:shadow-lg transition-all active:scale-75 duration-75 bg-white/80 dark:bg-gray-900/80 hover:bg-white dark:hover:bg-gray-800 border-0 flex items-center justify-center shrink-0 aspect-square"
                                    >
                                      <Heart 
                                        className={cn(
                                          "h-5 w-5 transition-all",
                                          isFavorited(item.products?.id || item.id) ? "fill-red-500 text-red-500" : "text-muted-foreground"
                                        )}
                                      />
                                    </Button>
                                  </div>
                                </div>

                                {/* Product Info */}
                                <div className="flex-1 min-w-0 max-w-full overflow-visible flex flex-col justify-between">
                                  <div>
                                    <h4
                                      className="text-sm font-semibold leading-none tracking-tight text-foreground line-clamp-2 group-hover:text-primary transition-colors cursor-pointer hover:underline"
                                      onClick={() => handleOpenProduct(item.product_id || item.products?.id)}
                                    >
                                      {loadingProductId === (item.product_id || item.products?.id) ? 'Cargando...' : (item.products?.title || 'Producto')}
                                    </h4>
                                    <div className="mt-2 flex flex-wrap items-end gap-3 min-w-0">
                                      <div className="flex flex-col gap-0.5 min-w-[7rem] flex-1 sm:flex-none overflow-hidden">
                                        <span className="text-xs text-muted-foreground leading-none">Unitario</span>
                                        <div className="truncate min-w-0">
                                          <FlipCounter 
                                            value={Math.round(unitPrice)} 
                                            className="text-sm font-bold text-foreground truncate"
                                            currencySymbol="$"
                                          />
                                        </div>
                                      </div>
                                      <div className="flex flex-col gap-0.5 min-w-[8.5rem] flex-1 sm:flex-none overflow-hidden">
                                        <span className="text-xs text-muted-foreground leading-none">Subtotal (x{item.quantity})</span>
                                        <div className="truncate min-w-0">
                                          <FlipCounter 
                                            value={Math.round(itemSubtotal)} 
                                            className="text-sm font-bold text-primary truncate"
                                            currencySymbol="$"
                                          />
                                        </div>
                                      </div>
                                      <div className="min-h-[44px] max-w-full flex items-center overflow-visible shrink-0">
                                        <QuantitySelector
                                          cartItemId={item.id}
                                          quantity={item.quantity}
                                          onUpdateQuantity={(cartItemId, quantityCalculator) => {
                                            void handleUpdateQuantity(cartItemId, quantityCalculator);
                                          }}
                                          onPlayBeep={playBeep}
                                          onVibrate={vibrate}
                                          disabled={updatingItemIds.has(item.id)}
                                        />
                                      </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground line-clamp-2 mt-2 leading-relaxed [overflow-wrap:anywhere]">
                                      {item.products?.description}
                                    </p>
                                    {item?.products?.product_type === 'tours' && passengerDataCompleted && (
                                      <div className="mt-2 w-full min-w-0 max-w-full overflow-hidden rounded-lg border border-emerald-200 bg-emerald-50/60 p-2.5 space-y-1.5">
                                        <p className="text-[11px] font-semibold text-emerald-900">{passengerEntries.length === 1 ? 'Pasajero cargado' : 'Pasajeros cargados'}</p>
                                        <p className="w-full min-w-0 max-w-full text-[11px] text-emerald-800 line-clamp-3 [overflow-wrap:anywhere] break-all">
                                          {passengerEntries.join(' | ')}
                                        </p>
                                        <p className="text-[11px] font-semibold text-emerald-900">Día seleccionado</p>
                                        <div className="w-full min-w-0 max-w-full space-y-0.5">
                                          {itemItineraryDates.length > 0 ? (
                                            itemItineraryDates.map((date: string, i: number) => (
                                              <p key={i} className="w-full min-w-0 max-w-full text-[11px] text-emerald-800 [overflow-wrap:anywhere] break-all">{date}</p>
                                            ))
                                          ) : (
                                            <p className="w-full min-w-0 max-w-full text-[11px] text-emerald-800/80 [overflow-wrap:anywhere]">Sin fecha asignada</p>
                                          )}
                                        </div>
                                        {passengerMeta?.pickup?.displayName && (
                                          <>
                                            <p className="text-[11px] font-semibold text-emerald-900">Punto de retiro</p>
                                            <p className="w-full min-w-0 max-w-full text-[11px] text-emerald-800 [overflow-wrap:anywhere] break-all">
                                              {passengerMeta.pickup.displayName}
                                              {(passengerMeta.pickup.floor || passengerMeta.pickup.apartment) && (
                                                <span className="text-emerald-700">
                                                  {' · '}
                                                  {[passengerMeta.pickup.floor ? `Piso ${passengerMeta.pickup.floor}` : null, passengerMeta.pickup.apartment ? `Depto ${passengerMeta.pickup.apartment}` : null].filter(Boolean).join(' · ')}
                                                </span>
                                              )}
                                            </p>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {(isSharedCollabCart || hasExternalCollaboratorActivity) && (
                          <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
                            <p className="text-[11px] font-semibold text-primary mb-2">Actividad colaborativa</p>
                            <div className="max-h-36 overflow-y-auto pr-1 space-y-1.5">
                              {sharedActivityLogs.length === 0 ? (
                                <p className="text-[11px] text-muted-foreground">Sin actividad reciente.</p>
                              ) : (
                                sharedActivityLogs.map((log) => {
                                  let hourLabel = '--:--';
                                  try {
                                    hourLabel = format(parseISO(log.created_at), 'HH:mm');
                                  } catch {
                                    hourLabel = '--:--';
                                  }

                                  return (
                                    <p key={log.id} className="text-[11px] text-muted-foreground break-words">
                                      <span className="text-primary/80 font-semibold">{hourLabel}</span>
                                      {' · '}
                                      {getActivityMessage(log)}
                                    </p>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}

                        {/* Store Rental: mode selector + calendar */}
                        {(() => {
                          const hasStoreRental = (cart?.cart_items || []).some((item: any) => isStoreRentalItem(item));
                          if (!hasStoreRental) return null;
                          const hasAnyRental = (cart?.cart_items || []).some((item: any) => getDailyPrice(item) !== null);
                          const currentMode = rentalModeByCartId[cart.id] ?? null;
                          const currentStoreId = rentalStoreByCartId[cart.id] ?? null;
                          const rentalDates = rentalDatesByCartId[cart.id];
                          const rentalQty = Math.max(
                            (cart?.cart_items || []).reduce((s: number, it: any) => s + (getDailyPrice(it) !== null ? Number(it.quantity || 1) : 0), 0),
                            1
                          );
                          const calendarDays = buildCalendarDays(rentalDates?.startDate || toLocalDateInputSidebar(new Date()), rentalQty);

                          return (
                            <div className="space-y-2">
                              {/* Mode selector */}
                              <div className="rounded-xl border border-border/50 bg-muted/30 p-3 space-y-3">
                                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Alquiler de tienda</p>
                                <div className="grid grid-cols-2 gap-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setRentalModeByCartId((prev) => ({ ...prev, [cart.id]: 'nueva' }));
                                      setRentalModeValidationByCartId((prev) => ({ ...prev, [cart.id]: false }));
                                    }}
                                    className={cn(
                                      'flex items-center gap-2 rounded-lg border px-3 py-2.5 text-xs font-semibold transition-all',
                                      currentMode === 'nueva'
                                        ? 'border-primary bg-primary/10 text-primary'
                                        : 'border-border/50 bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground/90 dark:hover:text-foreground'
                                    )}
                                  >
                                    <PlusCircle className="h-3.5 w-3.5 shrink-0" />
                                    <span>Nueva tienda</span>
                                    {currentMode === 'nueva' && <Check className="h-3 w-3 ml-auto shrink-0" />}
                                  </button>
                                  <button
                                    type="button"
                                    disabled={!loadingUserStores && userStores.length === 0}
                                    onClick={() => {
                                      setRentalModeByCartId((prev) => ({ ...prev, [cart.id]: 'renovar' }));
                                      setRentalModeValidationByCartId((prev) => ({ ...prev, [cart.id]: false }));
                                    }}
                                    className={cn(
                                      'flex items-center gap-2 rounded-lg border px-3 py-2.5 text-xs font-semibold transition-all',
                                      !loadingUserStores && userStores.length === 0
                                        ? 'border-border/30 bg-muted/20 text-muted-foreground/40 cursor-not-allowed'
                                        : currentMode === 'renovar'
                                        ? 'border-primary bg-primary/10 text-primary'
                                        : 'border-border/50 bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground/90 dark:hover:text-foreground'
                                    )}
                                  >
                                    <RotateCcw className="h-3.5 w-3.5 shrink-0" />
                                    <span>Renovar</span>
                                    {currentMode === 'renovar' && <Check className="h-3 w-3 ml-auto shrink-0" />}
                                  </button>
                                </div>

                                {currentMode === 'renovar' && (
                                  <div className="space-y-1.5">
                                    <p className="text-[11px] font-medium text-muted-foreground">Tienda a renovar</p>
                                    {loadingUserStores ? (
                                      <div className="flex items-center gap-2 py-2">
                                        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                                        <span className="text-xs text-muted-foreground">Cargando tiendas...</span>
                                      </div>
                                    ) : userStores.length === 0 ? (
                                      <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
                                        <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                                        <span className="text-xs text-destructive">No tenés tiendas existentes. Elegí Nueva tienda.</span>
                                      </div>
                                    ) : (
                                      <div className="space-y-1 max-h-36 overflow-y-auto">
                                        {userStores.map((s) => (
                                          <button
                                            key={s.id}
                                            type="button"
                                            onClick={() => setRentalStoreByCartId((prev) => ({ ...prev, [cart.id]: s.id }))}
                                            className={cn(
                                              'w-full flex items-center gap-2.5 rounded-lg border px-3 py-2 text-xs text-left transition-all',
                                              currentStoreId === s.id
                                                ? 'border-primary bg-primary/10 text-primary font-semibold'
                                                : 'border-border/40 bg-background text-foreground hover:border-primary/30'
                                            )}
                                          >
                                            <StoreIcon className="h-3.5 w-3.5 shrink-0" />
                                            <span className="truncate flex-1">{s.name}</span>
                                            {currentStoreId === s.id && <Check className="h-3.5 w-3.5 shrink-0" />}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {!currentMode && rentalModeValidationByCartId[cart.id] && (
                                  <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
                                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                                    <span className="text-xs text-destructive font-medium">Seleccioná una opción para continuar</span>
                                  </div>
                                )}
                              </div>

                              {/* Rental period */}
                              {hasAnyRental && (
                                <div className="rounded-xl border border-border/50 bg-muted/30 p-3 space-y-3">
                                  <div className="flex items-center gap-2">
                                    <CalendarDays className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Período de alquiler</p>
                                  </div>
                                  <label className="flex flex-col gap-1.5">
                                    <span className="text-xs font-medium text-muted-foreground">Fecha de inicio</span>
                                    <input
                                      type="date"
                                      value={rentalDates?.startDate || ''}
                                      onChange={(e) => {
                                        const nextStart = e.target.value;
                                        setRentalDatesByCartId((prev) => ({
                                          ...prev,
                                          [cart.id]: {
                                            startDate: nextStart,
                                            endDate: shiftLocalDateInputSidebar(nextStart, rentalQty - 1),
                                          },
                                        }));
                                      }}
                                      className="h-9 rounded-lg border border-border/60 bg-background px-3 text-sm font-medium text-foreground focus:border-primary focus:ring-1 focus:ring-primary/30 transition-colors outline-none"
                                    />
                                  </label>
                                  {calendarDays.length > 0 && (
                                    <div className="space-y-1.5">
                                      <p className="text-[11px] text-muted-foreground">
                                        <span className="font-semibold text-foreground">{calendarDays.length}</span> {calendarDays.length === 1 ? 'día' : 'días'} seleccionado{calendarDays.length !== 1 ? 's' : ''}
                                      </p>
                                      <div className="grid grid-cols-7 gap-1">
                                        {calendarDays.map((day, idx) => {
                                          const isFirst = idx === 0;
                                          const isLast = idx === calendarDays.length - 1;
                                          return (
                                            <div
                                              key={day.iso}
                                              className={cn(
                                                'rounded-md py-1.5 text-center text-[10px] font-bold leading-tight',
                                                isFirst
                                                  ? 'bg-primary text-primary-foreground'
                                                  : isLast && calendarDays.length > 1
                                                  ? 'bg-primary/60 text-primary-foreground'
                                                  : 'bg-muted text-muted-foreground'
                                              )}
                                            >
                                              <div className="uppercase opacity-75">{day.weekday}</div>
                                              <div>{day.label}</div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Cart Total and Pay Button */}
                        <div className="bg-muted/40 rounded-xl border border-primary/20 p-4 space-y-3">
                          {/* Total with FlipCounter */}
                          <div className="flex flex-col gap-2">
                            <div className="flex items-baseline justify-between gap-2 min-w-0">
                              <span className="text-sm font-semibold text-muted-foreground shrink-0">Total del canasto:</span>
                              <div className="text-right min-w-0 max-w-[8rem] overflow-hidden">
                                <FlipCounter 
                                  value={Math.round(cartTotal)} 
                                  className="text-xl font-bold text-primary truncate"
                                  currencySymbol="$"
                                />
                              </div>
                            </div>
                            <span className="text-xs text-muted-foreground bg-background px-2 py-1 rounded w-fit shrink-0">
                              {unitCount} {unitCount === 1 ? 'unidad' : 'unidades'}
                            </span>
                          </div>

                          {/* Pay Button */}
                          {hasTourItems ? (
                            passengerDataCompleted ? (
                              <Button
                                onClick={() => { void handlePayClick(cart); }}
                                className="w-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold flex items-center justify-center gap-2"
                              >
                                <CreditCard className="h-4 w-4" />
                                Pagar
                                <ChevronRight className="h-4 w-4 animate-[bounce-x_2s_ease-in-out_infinite]" />
                              </Button>
                            ) : (
                              <Button
                                onClick={() => { void handlePayClick(cart); }}
                                className="w-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold flex items-center justify-center gap-2"
                              >
                                <CreditCard className="h-4 w-4" />
                                Completar datos de pasajeros
                                <ChevronRight className="h-4 w-4 animate-[bounce-x_2s_ease-in-out_infinite]" />
                              </Button>
                            )
                          ) : (
                            <Button
                              onClick={() => { void handlePayClick(cart); }}
                              className="w-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold flex items-center justify-center gap-2"
                            >
                              <CreditCard className="h-4 w-4" />
                              Pagar
                              <ChevronRight className="h-4 w-4 animate-[bounce-x_2s_ease-in-out_infinite]" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}

                {session?.user?.id && (
                  <div className={cn('space-y-3 pt-4', carts.length !== 1 && 'border-t border-border/30')}>
                    {loadingOrders && allInProgressOrders.length === 0 && (
                      <div className="flex items-center gap-2 py-2 text-muted-foreground text-sm">
                        <Loader2 className="h-4 w-4 animate-spin text-primary opacity-70" />
                        <span>Cargando pedidos...</span>
                      </div>
                    )}
                    {!loadingOrders && allInProgressOrders.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-foreground px-1">En proceso</h3>
                        {allInProgressOrders.map((order: any) => renderOrderCard(order))}
                      </div>
                    )}
                    <button
                      onClick={handleToggleOrdersHistory}
                      className="w-full rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-background to-background p-3.5 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between min-w-0 gap-2">
                        <h3 className="text-sm font-semibold text-foreground truncate">Historial de pedidos</h3>
                        <ChevronRight className={cn("h-5 w-5 text-muted-foreground shrink-0 transition-transform duration-200", showOrdersHistory ? "rotate-90" : "rotate-0")} />
                      </div>
                    </button>
                    {showOrdersHistory && (
                      <>
                        {loadingOrders ? (
                          <div className="flex flex-col items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-primary opacity-70" />
                            <p className="mt-3 text-muted-foreground font-medium text-sm">Cargando pedidos...</p>
                          </div>
                        ) : orders.some((o: any) => o.status === 'fulfilled' || o.status === 'cancelled') ? (
                          <div className="space-y-3">
                            <div className="flex flex-wrap items-center gap-2 min-w-0">
                              <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 p-0.5">
                                <button
                                  type="button"
                                  onClick={() => setOrdersViewMode('date')}
                                  className={cn(
                                    'px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all',
                                    ordersViewMode === 'date'
                                      ? 'bg-primary text-primary-foreground'
                                      : 'text-primary/60 hover:text-primary'
                                  )}
                                >
                                  Fecha
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setOrdersViewMode('store')}
                                  className={cn(
                                    'px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all',
                                    ordersViewMode === 'store'
                                      ? 'bg-primary text-primary-foreground'
                                      : 'text-primary/60 hover:text-primary'
                                  )}
                                >
                                  Tienda
                                </button>
                              </div>
                            </div>
                            {ordersViewMode === 'date' && (
                              <div className="flex flex-col gap-2 min-w-0">
                                <button
                                  type="button"
                                  onClick={() => setCalendarOpen(o => !o)}
                                  className={cn(
                                    'h-8 inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border px-3 text-[11px] font-semibold transition-colors self-start',
                                    ordersDateFilter
                                      ? 'border-primary/40 bg-primary/10 text-primary'
                                      : 'border-primary/20 bg-background text-muted-foreground hover:text-foreground hover:border-primary/40'
                                  )}
                                  aria-label="Filtrar por fecha"
                                >
                                  <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                                  <span className="truncate max-w-[8.5rem] sm:max-w-[10rem]">
                                    {ordersDateFilter
                                      ? format(parseISO(ordersDateFilter), "d MMM yyyy", { locale: es })
                                      : 'Abrir calendario'}
                                  </span>
                                  {ordersDateFilter && (
                                    <span
                                      role="button"
                                      aria-label="Limpiar fecha"
                                      onClick={(e) => { e.stopPropagation(); setOrdersDateFilter(''); setCalendarOpen(false); }}
                                      className="ml-0.5 flex items-center justify-center rounded-full hover:bg-primary/20 p-0.5"
                                    >
                                      <X className="h-3 w-3" />
                                    </span>
                                  )}
                                </button>
                                <AnimatePresence>
                                  {calendarOpen && (() => {
                                    const calStart = startOfMonth(calendarMonth);
                                    const calEnd = endOfMonth(calendarMonth);
                                    const calDaysList = eachDayOfInterval({ start: calStart, end: calEnd });
                                    const calPadding: (Date | null)[] = Array.from({ length: (getDay(calStart) + 6) % 7 }).map(() => null);
                                    const calDays: (Date | null)[] = [...calPadding, ...calDaysList];
                                    return (
                                      <motion.div
                                        key="inline-calendar"
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="overflow-hidden"
                                      >
                                        <div className="pt-1">
                                          <div className="flex items-center justify-between mb-3 px-1 min-w-0 gap-2">
                                            <h3 className="text-sm font-bold capitalize truncate">
                                              {format(calendarMonth, 'MMMM yyyy', { locale: es })}
                                            </h3>
                                            <div className="flex gap-1 shrink-0">
                                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCalendarMonth(m => subMonths(m, 1))}>
                                                <ChevronLeft className="w-4 h-4" />
                                              </Button>
                                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCalendarMonth(m => addMonths(m, 1))}>
                                                <ChevronRight className="w-4 h-4" />
                                              </Button>
                                            </div>
                                          </div>
                                          <div className="grid grid-cols-7 gap-1 text-center mb-1">
                                            {['LU','MA','MI','JU','VI','SA','DO'].map(d => (
                                              <span key={d} className="text-[10px] font-bold text-muted-foreground/60">{d}</span>
                                            ))}
                                          </div>
                                          <div className="grid grid-cols-7 gap-1">
                                            {calDays.map((day, i) => {
                                              if (!day) return <div key={`e-${i}`} className="aspect-square" />;
                                              const dateStr = format(day, 'yyyy-MM-dd');
                                              const isToday = isSameDay(day, new Date());
                                              const isSelected = ordersDateFilter === dateStr;
                                              return (
                                                <button
                                                  key={dateStr}
                                                  type="button"
                                                  onClick={() => { setOrdersDateFilter(dateStr); setCalendarOpen(false); }}
                                                  className={cn(
                                                    'aspect-square flex items-center justify-center rounded-lg text-xs transition-all',
                                                    isSelected
                                                      ? 'bg-primary text-primary-foreground font-bold shadow-md shadow-primary/20 scale-105 z-10'
                                                      : isToday
                                                        ? 'bg-muted/80 text-primary font-bold border border-primary/20'
                                                        : 'hover:bg-muted active:scale-95'
                                                  )}
                                                >
                                                  {format(day, 'd')}
                                                </button>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      </motion.div>
                                    );
                                  })()}
                                </AnimatePresence>
                              </div>
                            )}
                            {ordersViewMode === 'store' ? (
                              visibleOrdersByStore.map((group) => {
                                const isExpanded = expandedStoreKeys.has(group.storeKey);
                                const toggleExpanded = () => {
                                  setExpandedStoreKeys((prev) => {
                                    const newSet = new Set(prev);
                                    if (newSet.has(group.storeKey)) {
                                      newSet.delete(group.storeKey);
                                    } else {
                                      newSet.add(group.storeKey);
                                    }
                                    return newSet;
                                  });
                                };
                                return (
                                  <div key={group.storeKey} className="space-y-3 pb-3 border-b border-border/30 last:border-b-0">
                                    <button
                                      onClick={toggleExpanded}
                                      className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-muted/50 rounded-lg border border-border/50 hover:bg-muted/70 transition-colors min-w-0"
                                    >
                                      <div className="flex items-center gap-2 flex-1 min-w-0 text-left">
                                        <StoreIcon className="h-4 w-4 text-primary shrink-0" />
                                        <h3 className="text-sm font-semibold text-foreground truncate max-w-full">{group.storeName}</h3>
                                      </div>
                                      <div className="flex items-center gap-2 shrink-0">
                                        <span className="text-xs font-semibold text-muted-foreground bg-muted px-2 py-1 rounded-full">
                                          {group.orders.length}
                                        </span>
                                        <ChevronRight
                                          className={cn(
                                            'h-4 w-4 text-muted-foreground transition-transform duration-200',
                                            isExpanded && 'rotate-90'
                                          )}
                                        />
                                      </div>
                                    </button>
                                    {isExpanded && (
                                      <div className="space-y-2">
                                        {group.orders.map((order: any) => renderOrderCard(order))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            ) : (
                              visibleOrders.map((order: any) => renderOrderCard(order))
                            )}
                            {visibleOrders.length === 0 && (
                              <div className="rounded-xl border border-dashed border-border bg-muted/30 px-3 py-4 text-center text-sm text-muted-foreground">
                                No hay pedidos para el filtro seleccionado.
                              </div>
                            )}
                            {hasMoreOrders && (
                              <Button
                                variant="outline"
                                className="w-full border-primary/30 bg-primary/5 hover:bg-primary/10"
                                onClick={() => setVisibleOrdersCount((prev) => prev + ORDERS_PAGE_SIZE)}
                              >
                                Cargar más pedidos
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-background to-background p-3.5 shadow-sm">
                            <p className="text-sm text-muted-foreground">No hay pedidos en tu historial.</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </ScrollArea>
          </motion.div>
          </>
      )}
    </AnimatePresence>
    </>
  );
};
